import "@glideapps/glide-data-grid/dist/index.css";

import {
  AllCellRenderers,
  // The bare editor rather than the convenience wrapper: the wrapper hardcodes
  // `renderers: AllCellRenderers` after spreading its props, so a replacement
  // marker renderer cannot be passed through it. Everything the wrapper adds is
  // re-supplied below, all of it public API.
  DataEditorCore as DataEditor,
  ImageWindowLoaderImpl,
  markerCellRenderer,
  sprites,
  type CellClickedEventArgs,
  type DrawCellCallback,
  type DrawHeaderCallback,
  type DataEditorRef,
  type EditableGridCell,
  type EditListItem,
  type FillPatternEventArgs,
  type GridCell,
  type HeaderClickedEventArgs,
  type GridKeyEventArgs,
  type GridSelection,
  type Item,
  type Rectangle,
} from "@glideapps/glide-data-grid";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import {
  forwardRef,
  useCallback,
  useDeferredValue,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { LoadingSpinner } from "@/core/components/loading-spinner";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/core/components/ui/alert/alert";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import { Tabs } from "@/core/components/ui/tabs";
import { toast } from "@/core/components/ui/toast";
import { useIsMobile } from "@/core/component-hooks/use-mobile";
import { cn } from "@/core/utils/utils";
import {
  CellCommentCard,
  type CellCommentCardTarget,
} from "@/payroll/ai-script/cell-comments/cell-comment-card";
import { buildCellCommentIndex } from "@/payroll/ai-script/cell-comments/cell-comment-index";
import { useCellCommentThreads } from "@/payroll/ai-script/cell-comments/query";
import {
  getCellKind,
  getRawInputKind,
  isFormulaCell,
} from "@/payroll/ai-script/cell-kind-utils";
import {
  aiScriptSpreadsheetKey,
  aiScriptSpreadsheetSheetKey,
  useBulkDeleteManualRows,
  useBulkPasteAiSpreadsheet,
  useBulkRestoreManualRows,
  useCreateManualRow,
  useConfirmAiSpreadsheetRegionOverride,
  useCreateAiSpreadsheetRegion,
  useDeleteAiSpreadsheetRegion,
  useDeleteManualRow,
  useDirectUpdateAiSpreadsheetCell,
  useGetAiScriptSpreadsheet,
  useLoadAiScriptSpreadsheetSheet,
  useRecoverAiScriptWorkbookOperation,
  useRestoreManualRow,
  useUpdateAiSpreadsheetColumnValueType,
  useUpdateAiSpreadsheetCell,
  useUpdateAiSpreadsheetRegion,
} from "@/payroll/ai-script/query";
import {
  buildOptimisticCell,
  findSheetResponseCell,
  patchSpreadsheetSheetCells,
} from "@/payroll/ai-script/spreadsheet-cache";
import {
  AI_SPREADSHEET_VALUE_TYPE,
  type AiScriptChatSelectionContext,
  type AiSpreadsheetValueType,
  type IAiScriptEditedCellSelection,
  type IAiSpreadsheetCell,
  type IAiSpreadsheetPasteEdit,
  type IAiSpreadsheetRegion,
  type IAiSpreadsheetRegionOverrideCandidate,
  type IAiSpreadsheetRegionOverrideReview,
  type IAiSpreadsheetSheetResponse,
} from "@/payroll/ai-script/types";
import {
  getAiScriptWorkbookAuthorizationMessage,
  isAiScriptWorkbookAuthorizationError,
  isAiScriptWorkbookVersionConflictError,
  isAiSpreadsheetSheetEditable,
} from "@/payroll/ai-script/utils";
import { flushWorkbookCellEdits } from "@/payroll/ai-script/workbook-cell-edit-batcher";
import type { WorkbookReadSource } from "@/payroll/ai-script/workbook-read-source";
import { SheetApprovalControl } from "@/payroll/approvals/sheet-approval-control";
import { getSheetApproval } from "@/payroll/approvals/utils";
import type {
  IPayrollSheetApprovalAccessKey,
  IPayrollSheetApprovalStatus,
  SheetApprovalType,
} from "@/payroll/payroll.d";
import {
  adaptSpreadsheetSheet,
  getCellFormatType,
  getInCellEditValue,
  isCellEditable,
  manualRowMarkerColor,
  overrideMarkerColor,
} from "./adapter";
import { getAiSpreadsheetCellDisplayValue } from "./cell-display-value";
import {
  createCellEditBatchFeedbackCollector,
  getCellEditNotification,
  getWorkbookMutationErrorFeedback,
  type WorkbookMutationErrorFeedback,
} from "./cell-edit-feedback";
import {
  drawColumnControlMarker,
  drawCopiedRangeBorder,
  drawSelectedHeaderBackground,
  drawSelectedRowMarkerBackground,
  drawCommentCornerMarker,
  drawOverrideCornerMarker,
} from "./cell-markers";
import {
  matchesMultiSelectModifier,
  toggleCellInSelection,
} from "./cell-selection";
import { drawFrozenColumnDivider } from "./column-freeze";
import {
  ColumnHeaderMenu,
  type ColumnHeaderMenuTarget,
} from "./column-header-menu";
import { ColumnValueTypeControl } from "./column-value-type-control";
import { EmptySheetState } from "./empty-sheet-state";
import { FormulaBar, type FormulaBarCommitTarget } from "./formula-bar";
import {
  buildFormulaFillEdits,
  isFormulaSourceRange,
  type FormulaFillRequest,
} from "./formula-fill";
import { openHyperlinkInNewTab } from "./hyperlink";
import { buildManualRowCells, getSheetContentBounds } from "./manual-row-utils";
import {
  type BufferedManualRowEdit,
  isOptimisticManualCell,
  optimisticManualCellBelongsTo,
  resolveBufferedManualRowEdits,
} from "./optimistic-manual-row-edits";
import { buildBulkClearPayload, buildBulkPastePayload } from "./paste-utils";
import { RowInsertMenu, type RowInsertMenuTarget } from "./row-insert-menu";
import { buildSelectionSummary } from "./selection-summary";
import { SelectionSummaryControl } from "./selection-summary-control";
import { SheetLoadingState } from "./sheet-loading-state";
import { SheetSearchControl } from "./sheet-search-control";
import { SheetTabList } from "./sheet-tab-list";
import {
  SpreadsheetColumnMenu,
  type SpreadsheetColumnMenuTarget,
} from "./spreadsheet-column-menu";
import {
  spreadsheetRegionHighlightRange,
  type SpreadsheetRegionDraft,
} from "./spreadsheet-region";
import { SpreadsheetRegionControl } from "./spreadsheet-region-control";
import { SpreadsheetRegionReviewBanner } from "./spreadsheet-region-review-banner";
import {
  buildSpreadsheetRegionRowMap,
  emptySpreadsheetRegionViewState,
  findDisplayedSpreadsheetRow,
  getSavedRowInsertAnchor,
  getSavedSpreadsheetRow,
  getSpreadsheetRegionColumnValues,
  hasActiveSpreadsheetRegionView,
  hasActiveSpreadsheetColumnFilter,
  projectAdaptedSpreadsheetSheet,
  resolveSpreadsheetRegionColumnType,
  type SpreadsheetRegionViewStates,
} from "./spreadsheet-region-view";
import { SpreadsheetRegionWorkbookReviewBanner } from "./spreadsheet-region-workbook-review-banner";
import {
  buildSpreadsheetTheme,
  volcaColor,
  volcaColorAlpha,
} from "./spreadsheet-theme";
import { suggestedValueCellRenderer } from "./suggested-value-cell";
import {
  getTabNavigationSelection,
  type TabNavigationTrigger,
} from "./tab-navigation";
import { drawTextOverflowCell } from "./text-overflow";
import { useFormulaFillInteractions } from "./use-formula-fill-interactions";
import { useGridViewportPreservation } from "./use-grid-viewport-preservation";
import { useRowResize } from "./use-row-resize";
import { useSheetLayout } from "./use-sheet-layout";
import { useSheetPrefetch } from "./use-sheet-prefetch";
import { useSheetSearch } from "./use-sheet-search";
import { useWorkbookClipboard } from "./use-workbook-clipboard";
import { useWorkbookHistory } from "./use-workbook-history";
import {
  buildAiScriptChatCellQuestion,
  buildAiScriptChatSelectionContext,
  emptySelection,
  formatCoordinate,
  getCellErrorCode,
  getEditedRawInput,
  getErrorTitle,
  getFormulaBarValue,
  scheduleGridRowScroll,
  VIEWER_OVERLAY_CLASSNAME,
} from "./utils";
import { resolveViewerShortcut } from "./viewer-shortcuts";
import {
  buildCellEditEntry,
  buildFormatEditEntry,
  buildManualRowCreateEntry,
  buildManualRowDeleteEntry,
  buildPasteEntry,
  matchesUndoRedoShortcut,
  replayOps,
  type ReplayOpMutations,
} from "./workbook-history-ops";
import type { WorkbookHistoryEntry } from "./workbook-history-types";

const CUSTOM_CELL_RENDERERS = [suggestedValueCellRenderer] as const;
const EMPTY_SPREADSHEET_REGIONS: IAiSpreadsheetRegion[] = [];
const EMPTY_REGION_OVERRIDE_REVIEWS: IAiSpreadsheetRegionOverrideReview[] = [];
const EMPTY_REGION_VIEW_STATES: SpreadsheetRegionViewStates = {};

// Pinned grid geometry. Glide otherwise derives the gutter width from the row
// count, so it would jump whenever a filter changes how many rows there are.
const SPREADSHEET_ROW_MARKER_WIDTH = 36;

const findRegionHeaderForRecord = (
  regions: IAiSpreadsheetRegion[],
  record: Pick<IAiSpreadsheetCell, "row" | "col">,
) =>
  regions.find(
    (region) =>
      region.validation.valid &&
      record.row === region.header_row &&
      record.col >= region.start_col &&
      record.col <= region.end_col,
  );

// A replayed op carries value_type as a bare string; narrow it to a known cell
// format before sending, dropping anything unrecognized rather than casting blind.
const VALID_VALUE_TYPES = new Set<string>(
  Object.values(AI_SPREADSHEET_VALUE_TYPE),
);
const asValueType = (
  value: string | undefined,
): AiSpreadsheetValueType | undefined =>
  value != null && VALID_VALUE_TYPES.has(value)
    ? (value as AiSpreadsheetValueType)
    : undefined;

const buildHistoryCellEdits = (
  sheetData: IAiSpreadsheetSheetResponse | undefined,
  edits: IAiSpreadsheetPasteEdit[],
) =>
  edits.map((edit) => {
    const prior = findSheetResponseCell(sheetData, edit.cell_id);
    return {
      cellId: edit.cell_id,
      priorRawInput: prior?.raw_input ?? "",
      nextRawInput: edit.raw_input,
      priorKind: getCellKind(prior),
      nextKind: edit.kind ?? getRawInputKind(edit.raw_input),
      priorValueType: asValueType(prior?.value_type ?? undefined),
      nextValueType: edit.value_type,
    };
  });

export interface AiScriptSpreadsheetViewerProps {
  readOnlySource?: WorkbookReadSource;
  businessId: string | null;
  runId: string;
  // When provided, sheet selection is controlled by the parent (for example,
  // from a URL search param). Consumers that omit it keep local sheet state.
  activeSheetId?: string | null;
  onActiveSheetChange?: (sheetId: string) => void;
  // When true, the (non-maximized) viewer fills its parent's height instead of
  // rendering at a fixed height — used when it's the primary surface of a pane
  // rather than an embedded card.
  fillHeight?: boolean;
  // Disable document-level grid find when a parent keeps this viewer mounted
  // but hidden. Visible viewers can omit this prop.
  isGlobalSearchEnabled?: boolean;
  // Reports the currently selected cell, row, or column (with sheet/header
  // context) so a parent surface — e.g. the explanation chat — can use it as
  // question context.
  onSelectionContextChange?: (
    context: AiScriptChatSelectionContext | null,
  ) => void;
  onAskAiAboutCell?: (context: AiScriptChatSelectionContext) => void;
  // Fired when the user clicks the formula-bar "View edit" action for a cell
  // that carries a persistent edit, so the parent opens the focused per-cell
  // review view. Selecting the cell alone no longer opens the sidebar.
  onViewEditedCell?: (selection: IAiScriptEditedCellSelection) => void;
  // Fired on every selection change with the selected cell's edit (or null when
  // the selection clears or lands on a cell without one), so the parent can
  // close a cell-opened sidebar once the user clicks off the edited cell.
  onEditedCellSelectionChange?: (
    selection: IAiScriptEditedCellSelection | null,
  ) => void;
  // Whether a side panel is open alongside the viewer. When maximized, the
  // fullscreen overlay reserves room on the right for it (side-by-side) instead
  // of painting over it. Consumers without a sidebar omit it (stays full-width).
  sidebarOpen?: boolean;
  // Fired when the user enters/exits fullscreen, so a parent coordinating a
  // side-by-side sidebar can mirror the state for its own layout.
  onMaximizedChange?: (maximized: boolean) => void;
  // Makes the grid view-only for everyone, including during a payroll run or
  // when the pay period is locked. Clearing it restores sheet permissions.
  isLocked?: boolean;
  approvalStatus?: IPayrollSheetApprovalStatus | null;
  onSheetApprovalChange?: (
    accessKey: IPayrollSheetApprovalAccessKey,
    approvalType: SheetApprovalType,
    enabled: boolean,
  ) => void;
  pendingSheetApproval?: {
    accessKeyId: string;
    approvalType: SheetApprovalType;
  } | null;
  // Lets users leave comments on cells (right-click → Comment). Commenting
  // never changes a figure, so it stays on for view-only sheets and locked
  // periods; a read-only source (harness, profitability) has no threads.
  commentsEnabled?: boolean;
}

export interface AiScriptSpreadsheetViewerHandle {
  // Clears the grid selection (e.g. when the chat detaches its selection context).
  clearSelection: () => void;
  // Switches to the given sheet and scrolls/selects the 1-based (row, col) cell.
  goToCell: (sheetId: string, row: number, col: number) => void;
  // Like goToCell, then opens the cell's comment card once the grid lands there
  // — resolved threads included, which selecting alone would not open.
  openCommentsAt: (sheetId: string, row: number, col: number) => void;
  // Clears the undo/redo stacks — used after a durable "Discard all" that the
  // session history can no longer meaningfully replay against.
  resetHistory: () => void;
}

export const AiScriptSpreadsheetViewer = forwardRef<
  AiScriptSpreadsheetViewerHandle,
  AiScriptSpreadsheetViewerProps
>(function AiScriptSpreadsheetViewer(
  {
    businessId,
    runId,
    activeSheetId: controlledActiveSheetId,
    onActiveSheetChange,
    fillHeight = false,
    isGlobalSearchEnabled = true,
    onSelectionContextChange,
    onAskAiAboutCell,
    onViewEditedCell,
    onEditedCellSelectionChange,
    sidebarOpen = false,
    onMaximizedChange,
    isLocked: locked = false,
    readOnlySource,
    approvalStatus,
    onSheetApprovalChange,
    pendingSheetApproval,
    commentsEnabled = false,
  },
  ref,
) {
  const isLocked = locked || !!readOnlySource;
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const viewerRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<DataEditorRef>(null);
  const gridWrapperRef = useRef<HTMLDivElement>(null);
  // Edits made while a manual row still has synthetic cell ids. Each live add
  // registers a token before rendering its optimistic row; edits are painted
  // locally and replayed after creation returns the row's real server ids.
  const bufferedManualRowEditsRef = useRef(
    new Map<string, Map<number, BufferedManualRowEdit>>(),
  );
  // Holds the in-flight go-to-cell scroll's cancel fn. Kept in a ref (not the
  // effect's cleanup) because clearing pendingCell re-runs that effect, and a
  // cleanup there would cancel the overshoot-correction timers before they fire.
  const scrollCancelRef = useRef<(() => void) | null>(null);
  const { data, isLoading, error } = useGetAiScriptSpreadsheet(
    businessId,
    runId,
    readOnlySource,
  );
  const workbookOperation = useRecoverAiScriptWorkbookOperation(
    readOnlySource ? null : businessId,
    readOnlySource ? null : runId,
  );
  const pendingWorkbookMutations = useIsMutating({
    predicate: (mutation) => {
      const variables = mutation.state.variables as
        | { runId?: string }
        | undefined;
      return variables?.runId === runId;
    },
  });

  const { mutateAsync: updateCellAsync, isPending: isUpdatingCell } =
    useUpdateAiSpreadsheetCell(businessId);
  const {
    mutateAsync: directUpdateCellAsync,
    isPending: isUpdatingCellDirectly,
  } = useDirectUpdateAiSpreadsheetCell(businessId);
  const isUpdatingAnyCell = isUpdatingCell || isUpdatingCellDirectly;
  const cellEditFeedbackCollectorRef = useRef(
    createCellEditBatchFeedbackCollector(),
  );
  const {
    mutateAsync: updateColumnValueTypeAsync,
    isPending: isUpdatingColumnValueType,
  } = useUpdateAiSpreadsheetColumnValueType(businessId);
  const { mutateAsync: bulkPasteAsync, isPending: isBulkPasting } =
    useBulkPasteAiSpreadsheet(businessId);
  const createSpreadsheetRegion = useCreateAiSpreadsheetRegion(businessId);
  const updateSpreadsheetRegion = useUpdateAiSpreadsheetRegion(businessId);
  const deleteSpreadsheetRegion = useDeleteAiSpreadsheetRegion(businessId);
  const confirmSpreadsheetRegionOverride =
    useConfirmAiSpreadsheetRegionOverride(businessId);
  const {
    mutate: createManualRow,
    mutateAsync: createManualRowAsync,
    isPending: isCreatingManualRow,
  } = useCreateManualRow(businessId);
  const { mutate: deleteManualRow, mutateAsync: deleteManualRowAsync } =
    useDeleteManualRow(businessId);
  const { mutateAsync: restoreManualRowAsync } =
    useRestoreManualRow(businessId);
  const { mutateAsync: bulkDeleteRowsAsync } =
    useBulkDeleteManualRows(businessId);
  const { mutateAsync: bulkRestoreRowsAsync } =
    useBulkRestoreManualRows(businessId);
  const history = useWorkbookHistory();
  // A synchronous in-flight lock for undo/redo. `history.isApplying` is React
  // state, so back-to-back invocations in one tick would both read the stale
  // (pre-render) value and double-apply; this ref flips synchronously.
  const applyingRef = useRef(false);
  // The workbook's latest edit sequence, updated from every mutation response.
  // Undo/redo sends it as base_version so a stale replay is rejected (409).
  const workbookVersionRef = useRef<number | undefined>(undefined);
  const [uncontrolledActiveSheetId, setUncontrolledActiveSheetId] = useState<
    string | null
  >(null);
  const activeSheetId =
    controlledActiveSheetId === undefined
      ? uncontrolledActiveSheetId
      : controlledActiveSheetId;
  const selectSheet = useCallback(
    (sheetId: string) => {
      if (controlledActiveSheetId === undefined) {
        setUncontrolledActiveSheetId(sheetId);
      }
      onActiveSheetChange?.(sheetId);
    },
    [controlledActiveSheetId, onActiveSheetChange],
  );
  const [gridSelection, setGridSelection] =
    useState<GridSelection>(emptySelection);
  const selectionBeforePointerDownRef = useRef<GridSelection>(emptySelection);
  const [isMaximized, setIsMaximized] = useState(false);
  const [isSheetSwitcherOpen, setIsSheetSwitcherOpen] = useState(false);
  // A pending "go to cell" request; applied once its sheet is active (below).
  const [pendingCell, setPendingCell] = useState<{
    sheetId: string;
    row: number;
    col: number;
  } | null>(null);
  // Right-click "insert row" menu: anchored at the cursor, targeting a 0-based
  // grid row. `null` when closed.
  const [rowMenu, setRowMenu] = useState<RowInsertMenuTarget | null>(null);
  // The open cell-comment card, anchored next to its cell. `null` when closed.
  const [commentCard, setCommentCard] = useState<CellCommentCardTarget | null>(
    null,
  );
  // A card to open once a go-to-cell lands (see openCommentsAt).
  const pendingCommentOpenRef = useRef<{
    sheetId: string;
    row: number;
    col: number;
  } | null>(null);
  // Bumped as the grid scrolls while a card is open, so its anchor follows.
  const [commentCardTick, setCommentCardTick] = useState(0);
  const [columnHeaderMenu, setColumnHeaderMenu] =
    useState<ColumnHeaderMenuTarget | null>(null);
  const [spreadsheetColumnMenu, setSpreadsheetColumnMenu] =
    useState<SpreadsheetColumnMenuTarget | null>(null);
  const [regionPreview, setRegionPreview] =
    useState<SpreadsheetRegionDraft | null>(null);
  const [regionDialogTarget, setRegionDialogTarget] = useState<{
    sheetId: string;
    selection: GridSelection;
    existingRegion?: IAiSpreadsheetRegion;
  } | null>(null);
  const [regionViewStatesBySheet, setRegionViewStatesBySheet] = useState<
    Record<string, SpreadsheetRegionViewStates>
  >({});
  // Cursor position of the last context-menu gesture. Captured from the native
  // event (Glide's onCellContextMenu args carry no client coordinates), read
  // when opening the menu so it appears under the pointer.
  const lastContextPointerRef = useRef<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });

  // Clicking the already-selected row or column toggles it off.
  const handleGridSelectionChange = (newSelection: GridSelection) => {
    setGridSelection((currentSelection) => {
      const nextRow = newSelection.rows.first();
      const nextColumn = newSelection.columns.first();
      const isReselectedRow =
        nextRow !== undefined && nextRow === currentSelection.rows.first();
      const isReselectedColumn =
        nextColumn !== undefined &&
        nextColumn === currentSelection.columns.first();

      return isReselectedRow || isReselectedColumn
        ? emptySelection
        : newSelection;
    });
  };

  const handleMultiSelectCellClick = (
    cell: Item,
    event: CellClickedEventArgs,
  ) => {
    if (isMobile || !matchesMultiSelectModifier(event)) return false;

    event.preventDefault();
    setGridSelection(
      toggleCellInSelection(selectionBeforePointerDownRef.current, cell),
    );
    return true;
  };

  // While maximized, Escape exits full screen — but let an open cell/formula
  // editor swallow the first Escape (so it cancels the edit instead).
  useEffect(() => {
    if (!isMaximized) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      setIsMaximized(false);
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isMaximized]);

  // Notify a coordinating parent when fullscreen toggles so it can lay a sidebar
  // out beside the overlay. `isMaximized` stays owned here (a second consumer,
  // the onboarding outputs pane, has no sidebar to coordinate).
  useEffect(() => {
    onMaximizedChange?.(isMaximized);
  }, [isMaximized, onMaximizedChange]);

  const spreadsheet = data?.spreadsheet;
  const sheets = spreadsheet?.sheets ?? [];

  // Derive the active sheet from the user's explicit tab choice, falling back
  // to the first available sheet if that choice disappears after a refetch.
  const activeSheet =
    sheets.find((sheet) => sheet.id === activeSheetId) ?? sheets[0];
  const activeSheetValue = activeSheet?.id ?? "";
  // The approval unit behind the active tab, if that sheet is under approval at
  // all. Undefined simply means no approval control in the toolbar.
  const activeSheetApproval = getSheetApproval(activeSheet, approvalStatus);
  const shouldLoadActiveSheet = !!activeSheet && !activeSheet.empty;
  const { data: activeSheetData, error: activeSheetError } =
    useLoadAiScriptSpreadsheetSheet(
      businessId,
      runId,
      activeSheet?.id ?? null,
      shouldLoadActiveSheet,
      readOnlySource,
    );

  // Cells live only in the per-sheet query; the aggregate workbook query owns
  // tab metadata, so refetching it cannot clobber loaded cells.
  const activeLoadedSheet =
    activeSheetData?.sheet && activeSheetData.sheet.id === activeSheet?.id
      ? activeSheetData.sheet
      : null;
  const frozenColumn = activeLoadedSheet?.frozen_column ?? null;
  const { cancelViewportRestore, clearGridViewport, handleGridScrollCapture } =
    useGridViewportPreservation({
      activeSheet: activeLoadedSheet,
      gridWrapperRef,
    });

  const changeActiveSheet = (sheetId: string) => {
    clearGridViewport();
    selectSheet(sheetId);
    setPendingCell(null);
    setGridSelection(emptySelection);
    setRowMenu(null);
    setCommentCard(null);
    setColumnHeaderMenu(null);
    setSpreadsheetColumnMenu(null);
    setRegionPreview(null);
    setRegionDialogTarget(null);
    // History ops key edits by cell id within the active sheet, so a sheet
    // switch invalidates the stacks. (A run change remounts via key={runId}.)
    history.reset();
    workbookVersionRef.current = undefined;
  };

  useImperativeHandle(ref, () => ({
    clearSelection: () => setGridSelection(emptySelection),
    goToCell: (sheetId, row, col) => {
      if (sheetId && sheetId !== activeSheetValue) changeActiveSheet(sheetId);
      else clearGridViewport();
      setPendingCell({ sheetId, row, col });
    },
    openCommentsAt: (sheetId, row, col) => {
      const selected =
        selectedCol !== null && selectedRow !== null
          ? savedCellCoordinate(selectedCol, selectedRow)
          : null;
      // Already selected: the selection will not change, so open the card now —
      // and still scroll, since the cell may have been scrolled out of view.
      if (
        sheetId === activeSheetValue &&
        selected?.row === row &&
        selected.col === col &&
        selectedCol !== null &&
        selectedRow !== null
      ) {
        clearGridViewport();
        setPendingCell({ sheetId, row, col });
        openCommentCard(selectedCol, selectedRow, false);
        return;
      }
      pendingCommentOpenRef.current = { sheetId, row, col };
      setCommentCard(null);
      if (sheetId && sheetId !== activeSheetValue) changeActiveSheet(sheetId);
      else clearGridViewport();
      setPendingCell({ sheetId, row, col });
    },
    resetHistory: () => history.reset(),
  }));
  const isActiveSheetEditable =
    isAiSpreadsheetSheetEditable(activeSheet) && !isLocked;
  const canMutateActiveSheet = isActiveSheetEditable;

  useEffect(() => {
    if (canMutateActiveSheet) return;
    setRegionDialogTarget(null);
    setRegionPreview(null);
  }, [canMutateActiveSheet]);
  // A phone cannot show a workbook, so it says so.
  //
  // A read-only workbook used to render as a pay statement here instead, projected
  // from whichever identity the server could infer. Nobody who reaches this screen
  // has a pay statement: technicians are routed to their own pack elsewhere, and
  // for the manager standing here the server fell back to the owner of the first
  // sheet they could see. It showed a manager somebody else's pay, quietly.
  //
  // Editable workbooks got the real grid, which at phone width is three columns of
  // twenty-seven with the formula bar cut off. Both are the same answer: come back
  // on a screen that fits.
  const isTooNarrowForGrid = isMobile;

  const baseAdaptedSheet = useMemo(
    () =>
      activeLoadedSheet
        ? adaptSpreadsheetSheet(activeLoadedSheet, {
            onHyperlinkClick: openHyperlinkInNewTab,
          })
        : null,
    [activeLoadedSheet],
  );
  const activeRegions = activeLoadedSheet?.regions ?? EMPTY_SPREADSHEET_REGIONS;
  const activeRegionOverrideReviews =
    activeLoadedSheet?.region_override_reviews ?? EMPTY_REGION_OVERRIDE_REVIEWS;
  const workbookRegionOverrideReviews =
    spreadsheet?.region_override_reviews ?? EMPTY_REGION_OVERRIDE_REVIEWS;
  const activeRegionViewStates =
    regionViewStatesBySheet[activeSheetValue] ?? EMPTY_REGION_VIEW_STATES;
  const regionRowMap = useMemo(
    () =>
      baseAdaptedSheet && activeLoadedSheet
        ? buildSpreadsheetRegionRowMap({
            rowCount: baseAdaptedSheet.rowCount,
            cells: activeLoadedSheet.cells,
            regions: activeRegions,
            viewStates: activeRegionViewStates,
          })
        : [],
    [
      activeLoadedSheet,
      activeRegionViewStates,
      activeRegions,
      baseAdaptedSheet,
    ],
  );
  const adaptedSheet = useMemo(
    () =>
      baseAdaptedSheet
        ? projectAdaptedSpreadsheetSheet(baseAdaptedSheet, regionRowMap)
        : null,
    [baseAdaptedSheet, regionRowMap],
  );
  const areCommentsEnabled = commentsEnabled && !readOnlySource && !!businessId;
  const { data: commentThreads } = useCellCommentThreads(
    businessId,
    runId,
    areCommentsEnabled,
  );
  const commentIndex = useMemo(
    () => buildCellCommentIndex(commentThreads, activeLoadedSheet?.id ?? null),
    [activeLoadedSheet?.id, commentThreads],
  );
  // A grid cell's SAVED (1-based) coordinate. Threads are placed by saved
  // coordinate, which a filter or sort moves away from the displayed row.
  const savedCellCoordinate = useCallback(
    (col: number, gridRow: number, record?: IAiSpreadsheetCell) => {
      const cell = record ?? adaptedSheet?.getCellRecord(col, gridRow);
      return cell
        ? { row: cell.row, col: cell.col }
        : {
            row: getSavedSpreadsheetRow(regionRowMap, gridRow) + 1,
            col: col + 1,
          };
    },
    [adaptedSheet, regionRowMap],
  );
  const openCommentCard = useCallback(
    (col: number, gridRow: number, focusComposer: boolean) => {
      if (!activeLoadedSheet) return;

      const record = adaptedSheet?.getCellRecord(col, gridRow);
      const saved = savedCellCoordinate(col, gridRow, record);
      const value = record
        ? getAiSpreadsheetCellDisplayValue(record).trim()
        : "";
      setCommentCard({
        sheetId: activeLoadedSheet.id,
        row: saved.row,
        col: saved.col,
        gridCol: col,
        gridRow,
        address: formatCoordinate([saved.col - 1, saved.row - 1]),
        context: value ? { value } : undefined,
        focusComposer,
      });
    },
    [activeLoadedSheet, adaptedSheet, savedCellCoordinate],
  );
  // Re-read the card's anchor whenever the grid scrolls while it is open —
  // including the scripted scroll a go-to-cell runs, whose last correction
  // lands a few hundred ms after it starts.
  const isCommentCardOpen = commentCard !== null;
  useEffect(() => {
    const wrapper = gridWrapperRef.current;
    if (!isCommentCardOpen || !wrapper) return undefined;

    let frame = 0;
    const bump = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() =>
        setCommentCardTick((tick) => tick + 1),
      );
    };
    const settle = window.setTimeout(bump, 300);
    wrapper.addEventListener("scroll", bump, { capture: true, passive: true });
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(settle);
      wrapper.removeEventListener("scroll", bump, { capture: true });
    };
  }, [isCommentCardOpen, commentCard]);
  // Where the open card sits: beside its cell, or nowhere while the cell is
  // scrolled out of the grid's view.
  const commentCardAnchor = useMemo(() => {
    if (!commentCard) return null;
    const bounds = gridRef.current?.getBounds(
      commentCard.gridCol,
      commentCard.gridRow,
    );
    const frame = gridWrapperRef.current?.getBoundingClientRect();
    if (!bounds || !frame) return null;
    const right = bounds.x + bounds.width;
    if (
      bounds.y + bounds.height < frame.top ||
      bounds.y > frame.bottom ||
      right < frame.left ||
      bounds.x > frame.right
    ) {
      return null;
    }
    return {
      x: Math.min(right, frame.right),
      y: Math.max(bounds.y, frame.top),
    };
    // commentCardTick and isMaximized re-read the grid's layout after it moves.
  }, [commentCard, commentCardTick, isMaximized, sidebarOpen]);
  const hasActiveRegionView = Object.values(activeRegionViewStates).some(
    hasActiveSpreadsheetRegionView,
  );

  /**
   * Keep the row-number gutter showing each row's SAVED row number while a filter
   * or sort is on. Glide numbers the gutter by screen position, which under a
   * projection disagrees with the address box and with every formula on the sheet
   * — row 4 showing "4" when it is really row 145 is worse than no number.
   *
   * The number Glide prints is just `cell.row`, so the fix is to hand its own
   * marker renderer a different one. Swapping a single entry in the default
   * renderer set keeps all of Glide's marker behaviour (hover, click-to-select,
   * checkbox styling) and needs no changes to column indices.
   */
  // One loader for the life of the grid, matching what the wrapper would have made.
  const gridImageWindowLoader = useMemo(() => new ImageWindowLoaderImpl(), []);

  // Read once: the tokens come off :root, which does not change while mounted.
  const gridTheme = useMemo(() => buildSpreadsheetTheme(), []);

  // The display row holding the selected cell, read at paint time by the
  // row-marker renderer so it can mark the row axis the way Glide already marks
  // the column axis.
  const selectedRowRef = useRef<number | null>(null);
  selectedRowRef.current = gridSelection.current?.cell[1] ?? null;
  // Whole-row selection is a separate thing from the cursor's row: clicking the
  // row number puts the row in `rows` and leaves `current` where it was.
  const selectedRowsRef = useRef<GridSelection["rows"] | null>(null);
  selectedRowsRef.current = gridSelection.rows;
  // Every rectangle the selection covers, so both axes can mark a dragged range
  // and not just its anchor. `rangeStack` carries the extra rectangles a
  // Ctrl/Cmd-click adds under `rangeSelect="multi-rect"`.
  const selectedRangesRef = useRef<readonly Rectangle[]>([]);
  selectedRangesRef.current = gridSelection.current
    ? [gridSelection.current.range, ...gridSelection.current.rangeStack]
    : [];

  /**
   * Does a selected range pass through this column / row?
   *
   * Read from a ref for the same reason the row marker is: these run inside
   * per-cell draw callbacks on every frame, and depending on the selection would
   * rebuild the renderer registry on every arrow-key press.
   */
  const isColumnInSelectedRange = useCallback(
    (columnIndex: number) =>
      selectedRangesRef.current.some(
        (range) =>
          columnIndex >= range.x && columnIndex < range.x + range.width,
      ),
    [],
  );
  const isRowInSelectedRange = useCallback(
    (rowIndex: number) =>
      selectedRangesRef.current.some(
        (range) => rowIndex >= range.y && rowIndex < range.y + range.height,
      ),
    [],
  );

  const gridRenderers = useMemo(() => {
    // Widened to the list's element type exactly as Glide does for its own
    // renderers: each is written against its specific cell, the registry holds
    // them as the union.
    const rowMarkerRenderer = {
      ...markerCellRenderer,
      ...(hasActiveRegionView
        ? {
            getAccessibilityString: (
              cell: Parameters<
                NonNullable<typeof markerCellRenderer.getAccessibilityString>
              >[0],
            ) =>
              String(
                getSavedSpreadsheetRow(
                  regionRowMap,
                  Math.max(0, cell.row - 1),
                ) + 1,
              ),
          }
        : {}),
      draw: (args: Parameters<typeof markerCellRenderer.draw>[0]) => {
        // Mark the row axis. Read from a ref rather than a dependency: the
        // registry would otherwise be rebuilt on every arrow-key press, churning
        // Glide's renderer map for what is a one-cell repaint.
        const isWholeRowSelected =
          selectedRowsRef.current?.hasIndex(args.row) ?? false;
        const isSelectedRow =
          isWholeRowSelected ||
          isRowInSelectedRange(args.row) ||
          args.row === selectedRowRef.current;
        if (isSelectedRow) {
          drawSelectedRowMarkerBackground(
            args.ctx,
            args.rect,
            isWholeRowSelected,
          );
        }

        const savedRow = hasActiveRegionView ? regionRowMap[args.row] : null;
        return markerCellRenderer.draw(
          {
            ...args,
            // Gutter numbers are deliberately faint chrome, but that ink over
            // the selected row's tint lands at 1.9:1 — the number naming the row
            // you are on became the hardest one to read. Full-strength ink on
            // the current row only, matching the selected column header's title.
            ...(isSelectedRow
              ? {
                  theme: {
                    ...args.theme,
                    textLight: volcaColor("--text-primary"),
                  },
                }
              : {}),
            // rowMap is zero-based; the gutter is one-based, like a formula.
            ...(savedRow == null
              ? {}
              : { cell: { ...args.cell, row: savedRow + 1 } }),
          },
          args.cell,
        );
      },
    } as (typeof AllCellRenderers)[number];

    return AllCellRenderers.map((renderer) =>
      renderer.kind === markerCellRenderer.kind ? rowMarkerRenderer : renderer,
    );
  }, [hasActiveRegionView, isRowInSelectedRange, regionRowMap]);

  // The last row/column that actually holds data (1-based), used to place added
  // rows and to tell a paste-beyond-the-data apart from an interior blank.
  const { maxContentRow, maxContentCol } = useMemo(
    () => getSheetContentBounds(activeLoadedSheet?.cells ?? []),
    [activeLoadedSheet],
  );

  /**
   * `maxContentRow` counts SAVED rows, but every coordinate the grid hands a
   * handler is a DISPLAY row, and a region view makes the two diverge. Anything
   * that bounds an iteration over grid coordinates has to use this instead;
   * cells inside it that hold nothing still resolve to no record and are skipped,
   * so over-counting is safe where under-counting would silently drop edits.
   */
  const maxDisplayRow = hasActiveRegionView
    ? (adaptedSheet?.rowCount ?? maxContentRow)
    : maxContentRow;

  const {
    clearCopiedSelection,
    consumePasteMetadata,
    copiedSelection,
    handleCopyCapture,
    handlePasteCapture,
  } = useWorkbookClipboard({
    activeSheetId: activeSheetValue,
    adaptedSheet,
    gridSelection,
  });
  const isActiveSheetLoading =
    !!activeSheet && !activeSheet.empty && !adaptedSheet;
  const isActiveSheetEmpty =
    activeSheet?.empty || activeLoadedSheet?.cells.length === 0;
  const prefetchSheet = useSheetPrefetch({
    businessId: readOnlySource ? null : businessId,
    runId,
    sheets,
    activeSheetId: activeSheet?.id ?? null,
  });

  const showMutationErrorFeedback = (
    feedback: WorkbookMutationErrorFeedback,
  ) => {
    if (
      feedback.error instanceof Error &&
      feedback.error.name === "AbortError"
    ) {
      return;
    }

    if (isAiScriptWorkbookAuthorizationError(feedback.error)) {
      queryClient.invalidateQueries({
        queryKey: aiScriptSpreadsheetKey(businessId, runId),
      });
    }

    toast.error(feedback.title, {
      description: feedback.description,
      id: feedback.id,
    });
  };

  const handleMutationError = (title: string, mutationError: unknown) =>
    showMutationErrorFeedback(
      getWorkbookMutationErrorFeedback(title, mutationError),
    );

  const sheetLayout = useSheetLayout({
    businessId,
    runId,
    sheet: activeLoadedSheet,
    editable: isActiveSheetEditable,
    columns: adaptedSheet?.columns,
    onError: handleMutationError,
  });
  const { columns } = sheetLayout;

  const findRegionHeaderAtCell = (
    cell: Item,
  ): IAiSpreadsheetRegion | undefined => {
    const record = adaptedSheet?.getCellRecord(cell[0], cell[1]);
    if (!record) return undefined;

    return findRegionHeaderForRecord(activeRegions, record);
  };

  const openSpreadsheetColumnMenu = (
    cell: Item,
    position: { x: number; y: number },
    focusOnOpen = false,
  ): boolean => {
    const region = findRegionHeaderAtCell(cell);
    const record = adaptedSheet?.getCellRecord(cell[0], cell[1]);
    if (!region || !record || !activeLoadedSheet) return false;

    const setting = region.column_settings[String(record.col)];
    if (!setting?.filterable && !setting?.sortable) return false;

    setRowMenu(null);
    setColumnHeaderMenu(null);
    setRegionPreview(null);
    setSpreadsheetColumnMenu({
      ...position,
      column: record.col,
      region,
      values: getSpreadsheetRegionColumnValues(
        activeLoadedSheet.cells,
        region,
        record.col,
      ),
      columnType: resolveSpreadsheetRegionColumnType(
        activeLoadedSheet.cells,
        region,
        record.col,
      ),
      // The selected cell is the header, so its displayed text names the column.
      label: getAiSpreadsheetCellDisplayValue(record).trim(),
      focusOnOpen,
    });
    return true;
  };

  const navigateWithTab = (trigger: TabNavigationTrigger) => {
    const nextSelection = getTabNavigationSelection({
      columnCount: columns.length,
      rowCount: adaptedSheet?.rowCount ?? 0,
      selection: gridSelection,
      trigger,
    });
    if (!nextSelection?.current) return false;

    setGridSelection(nextSelection);
    const target = nextSelection.current.cell;
    gridRef.current?.scrollTo(target[0], target[1], "both");

    return true;
  };

  const handleGridKeyDown = (event: GridKeyEventArgs) => {
    if (event.key === "Escape" && copiedSelection) {
      clearCopiedSelection();
      return;
    }

    if (
      event.key === "ArrowDown" &&
      event.altKey &&
      gridSelection.current?.cell &&
      event.bounds &&
      openSpreadsheetColumnMenu(
        gridSelection.current.cell,
        {
          x: event.bounds.x,
          y: event.bounds.y + event.bounds.height,
        },
        true,
      )
    ) {
      event.cancel();
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const moved = navigateWithTab({
      mode: "selection",
      key: event.key,
      altKey: event.altKey,
      ctrlKey: event.ctrlKey,
      metaKey: event.metaKey,
      shiftKey: event.shiftKey,
    });
    if (!moved) return;

    // Cancel Glide's matching Tab keybinding so this controlled selection only
    // advances once, and own the browser event so focus remains inside the grid.
    event.cancel();
    event.preventDefault();
    event.stopPropagation();
  };

  const handleFinishedEditing = (
    _newValue: GridCell | undefined,
    movement: Item,
  ) => {
    // Glide commits the overlay value through onCellsEdited before invoking this
    // callback. Re-assert the controlled selection afterwards so an async cache
    // update cannot leave focus on the edited cell.
    navigateWithTab({ mode: "editing", movement });
  };

  const selectionSummaryInput = useMemo(
    () => ({ sheetId: activeSheet?.id ?? null, selection: gridSelection }),
    [activeSheet?.id, gridSelection],
  );
  const deferredSelectionSummaryInput = useDeferredValue(selectionSummaryInput);
  const selectionSummary = useMemo(
    () =>
      adaptedSheet && deferredSelectionSummaryInput.sheetId === activeSheet?.id
        ? buildSelectionSummary({
            selection: deferredSelectionSummaryInput.selection,
            rowCount: adaptedSheet.rowCount,
            columnCount: adaptedSheet.columns.length,
            getCellRecord: adaptedSheet.getCellRecord,
          })
        : null,
    [activeSheet?.id, adaptedSheet, deferredSelectionSummaryInput],
  );
  const selectedColumnIndex = gridSelection.columns.first();
  const selectedCell = gridSelection.current?.cell ?? null;
  const [selectedCol, selectedRow] = selectedCell ?? [null, null];

  // Selecting a cell with an open thread opens its card, like a spreadsheet;
  // moving to another cell closes a card left open on the previous one. It
  // re-evaluates once the threads arrive, so a cell selected before they
  // loaded still opens. A right-click selects too, but there the menu owns
  // the gesture — the card opens from its Comment action instead.
  const haveCommentThreadsLoaded = commentThreads !== undefined;
  useEffect(() => {
    if (!areCommentsEnabled || selectedCol === null || selectedRow === null) {
      return;
    }
    if (rowMenu) return;
    const saved = savedCellCoordinate(selectedCol, selectedRow);
    const pending = pendingCommentOpenRef.current;
    if (
      pending &&
      pending.sheetId === activeLoadedSheet?.id &&
      pending.row === saved.row &&
      pending.col === saved.col
    ) {
      pendingCommentOpenRef.current = null;
      openCommentCard(selectedCol, selectedRow, false);
      return;
    }
    if (commentIndex.hasOpenThreadAt(saved.row, saved.col)) {
      openCommentCard(selectedCol, selectedRow, false);
    } else {
      setCommentCard((current) =>
        current && (current.row !== saved.row || current.col !== saved.col)
          ? null
          : current,
      );
    }
    // Only a selection change (or the first load) re-evaluates; a thread
    // update must not reopen a card the user just closed.
  }, [areCommentsEnabled, haveCommentThreadsLoaded, selectedCol, selectedRow]);
  const selectedCellRecord =
    selectedCell && adaptedSheet
      ? adaptedSheet.getCellRecord(selectedCell[0], selectedCell[1])
      : undefined;
  // The blank row under the data is selected: typing a value (in the grid or
  // the formula bar) adds a row rather than editing an existing cell.
  const isNewRowSelected =
    !!selectedCell &&
    !selectedCellRecord &&
    !!adaptedSheet &&
    adaptedSheet.isAddRowCell(selectedCell[0], selectedCell[1]);
  const canEditSelectedCell =
    !!selectedCellRecord &&
    canMutateActiveSheet &&
    isCellEditable(selectedCellRecord);
  const formulaBarCommitTarget: FormulaBarCommitTarget | null =
    canEditSelectedCell && selectedCellRecord
      ? { kind: "cell", cellId: selectedCellRecord.id }
      : canMutateActiveSheet && isNewRowSelected && selectedCell
        ? { kind: "new-row", columnIndex: selectedCell[0] }
        : null;
  const {
    controlProps: sheetSearchControlProps,
    highlightRegions: searchHighlightRegions,
    navigationTarget: searchNavigationTarget,
    open: openSheetSearch,
  } = useSheetSearch({
    activeSheetId: activeSheet?.id ?? null,
    adaptedSheet,
  });
  const gridHighlightRegions = useMemo(() => {
    const previewRange = regionPreview
      ? spreadsheetRegionHighlightRange(regionPreview)
      : null;
    // Aqua, not the selection blue: the region dialog opens from a selection, so
    // this preview is painted over live blue cells and has to stay legible
    // against them. Aqua is the on-palette home for the Tailwind teal these were
    // before — composited at these opacities the two are within 7/255.
    const previewHighlights = previewRange
      ? [
          {
            color: volcaColorAlpha("--aqua-60", 0.33),
            range: { ...previewRange, height: 1 },
            style: "solid-outline" as const,
          },
          {
            color: volcaColorAlpha("--aqua-50", 0.2),
            range: {
              ...previewRange,
              y: previewRange.y + 1,
              height: Math.max(previewRange.height - 1, 1),
            },
            style: "solid" as const,
          },
        ]
      : [];

    return [...(searchHighlightRegions ?? []), ...previewHighlights];
  }, [regionPreview, searchHighlightRegions]);
  const isViewerShortcutScopeActive = useCallback(() => {
    if (isMaximized || isSheetSwitcherOpen) return true;

    const activeElement = document.activeElement;
    return (
      activeElement instanceof Node &&
      (viewerRef.current?.contains(activeElement) ?? false)
    );
  }, [isMaximized, isSheetSwitcherOpen]);

  const canOpenSheetSearch =
    isGlobalSearchEnabled &&
    !isTooNarrowForGrid &&
    !activeSheetError &&
    !isActiveSheetLoading &&
    !isActiveSheetEmpty &&
    !!adaptedSheet;

  // Viewer shortcuts are scoped to the active workbook surface. Cmd/Ctrl+F
  // is global while the interactive grid is open. Cmd/Ctrl+Shift+F toggles
  // full screen and Cmd/Ctrl+K toggles the sheet switcher while the viewer is
  // active.
  useEffect(() => {
    const handleViewerShortcut = (event: KeyboardEvent) => {
      const action = resolveViewerShortcut(event, {
        canOpenSheetSearch,
        isViewerActive: isViewerShortcutScopeActive(),
      });
      if (!action) return;

      event.preventDefault();
      switch (action) {
        case "open-sheet-search":
          openSheetSearch();
          break;
        case "toggle-maximized":
          setIsMaximized((prev) => !prev);
          break;
        case "toggle-sheet-switcher":
          setIsSheetSwitcherOpen((prev) => !prev);
          break;
      }
    };

    document.addEventListener("keydown", handleViewerShortcut);
    return () => document.removeEventListener("keydown", handleViewerShortcut);
  }, [canOpenSheetSearch, isViewerShortcutScopeActive, openSheetSearch]);

  // The cell's OWN saved coordinate, not where it currently sits on screen. Under
  // a filter or sort those differ, and the address has to name the row a formula
  // would actually reference — showing "B4" for saved row 67 is simply wrong.
  const selectedCoordinate = selectedCell
    ? formatCoordinate(
        selectedCellRecord
          ? [selectedCellRecord.col - 1, selectedCellRecord.row - 1]
          : selectedCell,
      )
    : null;

  useEffect(() => {
    if (!onSelectionContextChange) return;

    if (!adaptedSheet || !activeLoadedSheet) {
      onSelectionContextChange(null);
      return;
    }

    onSelectionContextChange(
      buildAiScriptChatSelectionContext({
        adaptedSheet,
        activeSheet: activeLoadedSheet,
        gridSelection,
        getSavedRow: (displayRow) =>
          getSavedSpreadsheetRow(regionRowMap, displayRow),
      }),
    );
  }, [
    gridSelection,
    adaptedSheet,
    activeLoadedSheet,
    regionRowMap,
    onSelectionContextChange,
  ]);

  // Report the selected cell's persistent edit (or null) on every selection
  // change. Selecting an edited cell surfaces the formula-bar "View edit"
  // action rather than opening the sidebar; this signal lets the parent close a
  // cell-opened sidebar once the user clicks off that cell.
  useEffect(() => {
    if (!onEditedCellSelectionChange || !adaptedSheet) return;

    const cell = gridSelection.current?.cell;
    const record = cell
      ? adaptedSheet.getCellRecord(cell[0], cell[1])
      : undefined;

    onEditedCellSelectionChange(
      cell && record?.override
        ? {
            override: record.override,
            coordinate: formatCoordinate([record.col - 1, record.row - 1]),
          }
        : null,
    );
  }, [gridSelection, adaptedSheet, onEditedCellSelectionChange]);

  // Apply a pending "go to cell" once its sheet is the active one (a sheet
  // switch loads asynchronously). Selects + scrolls the cell into view.
  useEffect(() => {
    if (
      !pendingCell ||
      activeSheet?.id !== pendingCell.sheetId ||
      !adaptedSheet
    ) {
      return;
    }

    const col = Math.max(0, pendingCell.col - 1);
    const savedRow = Math.max(0, pendingCell.row - 1);
    const projectedRow = findDisplayedSpreadsheetRow(regionRowMap, savedRow);
    if (hasActiveRegionView && projectedRow === null) {
      setRegionViewStatesBySheet((current) => ({
        ...current,
        [activeSheetValue]: {},
      }));
      return;
    }
    const row = projectedRow ?? savedRow;
    const cell: Item = [col, row];
    setGridSelection({
      ...emptySelection,
      current: {
        cell,
        range: { x: col, y: row, width: 1, height: 1 },
        rangeStack: [],
      },
    });
    setPendingCell(null);
    // Cancel any prior in-flight scroll, then schedule this one. We store the
    // cancel in a ref rather than returning it as the effect cleanup: setPendingCell
    // above re-runs this effect, and a cleanup would cancel the [40,120,220]ms
    // overshoot re-assert timers ~1 frame after scheduling — before they fire.
    scrollCancelRef.current?.();
    scrollCancelRef.current = scheduleGridRowScroll({
      gridWrapper: gridWrapperRef.current,
      row,
      getRowHeight: adaptedSheet.getRowHeight,
    });
  }, [
    pendingCell,
    activeSheet?.id,
    activeSheetValue,
    adaptedSheet,
    hasActiveRegionView,
    regionRowMap,
  ]);

  // Cancel a pending go-to-cell scroll only on unmount.
  useEffect(() => () => scrollCancelRef.current?.(), []);

  const formulaBarValue = getFormulaBarValue(selectedCellRecord);
  const selectedCellErrorCode = getCellErrorCode(selectedCellRecord);

  // The persistent edit on the currently selected cell, if any — drives the
  // formula-bar "View edit" action.
  const selectedEditedCell: IAiScriptEditedCellSelection | null =
    selectedCell && selectedCellRecord?.override && selectedCoordinate
      ? {
          override: selectedCellRecord.override,
          coordinate: selectedCoordinate,
        }
      : null;

  const drawCell = useCallback<DrawCellCallback>(
    (args, drawContent) => {
      drawTextOverflowCell({
        args,
        columns,
        drawContent,
        textOverflow: adaptedSheet?.getTextOverflow(args.col, args.row),
      });

      // Flag a persistent edit — or a filled cell in a user-added row — with the
      // same corner marker, so edits and added rows share one visual language.
      const record = adaptedSheet?.getCellRecord(args.col, args.row);
      if (record?.override) {
        drawOverrideCornerMarker(
          args.ctx,
          args.rect,
          overrideMarkerColor(record.override),
        );
      } else if (
        record?.manual_row_id &&
        record.computed_value != null &&
        String(record.computed_value) !== ""
      ) {
        drawOverrideCornerMarker(args.ctx, args.rect, manualRowMarkerColor());
      }
      if (areCommentsEnabled) {
        const saved = savedCellCoordinate(args.col, args.row, record);
        if (commentIndex.hasOpenThreadAt(saved.row, saved.col)) {
          drawCommentCornerMarker(args.ctx, args.rect);
        }
      }

      const headerRegion = record
        ? findRegionHeaderForRecord(activeRegions, record)
        : undefined;
      const headerSetting = headerRegion?.column_settings[String(record?.col)];
      if (
        record &&
        headerRegion &&
        (headerSetting?.filterable || headerSetting?.sortable)
      ) {
        const viewState = activeRegionViewStates[headerRegion.id];
        const filter = viewState?.filters[String(record.col)];
        const filtered = hasActiveSpreadsheetColumnFilter(filter);
        const sortDirection =
          viewState?.sort?.column === record.col
            ? viewState.sort.direction
            : null;

        drawColumnControlMarker(args.ctx, args.rect, {
          filtered,
          sortDirection,
          // The header's own resolved colors — a generated sheet styles its
          // header however the script chose, so a fixed grey would vanish on
          // half of them.
          textColor: args.theme.textDark,
          backgroundColor: args.theme.bgCell,
          label: getAiSpreadsheetCellDisplayValue(record),
          font: `${args.theme.baseFontStyle} ${args.theme.fontFamily}`,
        });
      }

      if (copiedSelection?.sheetId === activeSheetValue) {
        drawCopiedRangeBorder(
          args.ctx,
          args.rect,
          { col: args.col, row: args.row },
          copiedSelection.range,
        );
      }

      // Last, so the seam sits over the cell's own content and its right border.
      drawFrozenColumnDivider(args.ctx, args.rect, args.col, frozenColumn);
    },
    [
      activeRegionViewStates,
      activeRegions,
      activeSheetValue,
      adaptedSheet,
      areCommentsEnabled,
      columns,
      commentIndex,
      copiedSelection,
      frozenColumn,
      savedCellCoordinate,
    ],
  );

  /**
   * Header painting the theme cannot express.
   *
   * Two marks, both of which need to sit outside Glide's own header fill: a
   * selected column reads grey rather than a solid accent block, and the frozen
   * boundary carries the same rule the cells below it do.
   */
  const drawGridHeader = useCallback<DrawHeaderCallback>(
    (args, drawContent) => {
      if (args.isSelected || isColumnInSelectedRange(args.columnIndex)) {
        drawSelectedHeaderBackground(args.ctx, args.rect, args.isSelected);
      }

      drawContent();

      drawFrozenColumnDivider(
        args.ctx,
        args.rect,
        args.columnIndex,
        frozenColumn,
      );
    },
    [frozenColumn, isColumnInSelectedRange],
  );

  // Track the workbook's edit sequence off every mutation response, so undo/redo
  // can send the freshest value as base_version.
  const recordWorkbookVersion = (version: number | undefined) => {
    if (version != null) workbookVersionRef.current = version;
  };

  // Look up a loaded cell by its id (the history ops key edits by cell id, while
  // the adapter keys by grid coordinate). Used to capture the prior raw input
  // before an edit and to re-send an unchanged input when replaying a reformat.
  const findLoadedCellById = (cellId: string): IAiSpreadsheetCell | undefined =>
    activeLoadedSheet?.cells.find((cell) => cell.id === cellId);

  const coordinateForCell = (cell: IAiSpreadsheetCell): string =>
    formatCoordinate([cell.col - 1, cell.row - 1]);

  // Undo/redo waits for each direct mutation so it can thread base_version
  // through the replay. Live value edits use promises from the batch queue.
  const replayMutations: ReplayOpMutations = {
    updateCell: (params) =>
      directUpdateCellAsync({
        runId,
        sheetId: params.sheetId,
        cellId: params.cellId,
        rawInput: params.rawInput,
        kind: params.kind,
        valueType: asValueType(params.valueType),
        baseVersion: params.baseVersion,
      }),
    // Undo/redo of a paste (or multi-cell clear) restores every cell in ONE bulk
    // request, not one per cell.
    bulkPaste: (params) =>
      bulkPasteAsync({
        runId,
        sheetId: params.sheetId,
        edits: params.edits,
        rows: [],
        baseVersion: params.baseVersion,
      }),
    createManualRow: (params) =>
      createManualRowAsync({
        runId,
        sheetId: params.sheetId,
        rows: params.rows,
        baseVersion: params.baseVersion,
      }),
    deleteManualRow: (params) =>
      deleteManualRowAsync({
        runId,
        sheetId: params.sheetId,
        manualRowId: params.manualRowId,
        baseVersion: params.baseVersion,
      }),
    restoreManualRow: (params) =>
      restoreManualRowAsync({
        runId,
        sheetId: params.sheetId,
        manualRowId: params.manualRowId,
        baseVersion: params.baseVersion,
      }),
    // Undo/redo of a paste that created rows deletes/restores them all in ONE
    // request, not one per row.
    bulkDeleteRows: (params) =>
      bulkDeleteRowsAsync({
        runId,
        sheetId: params.sheetId,
        manualRowIds: params.manualRowIds,
        baseVersion: params.baseVersion,
      }),
    bulkRestoreRows: (params) =>
      bulkRestoreRowsAsync({
        runId,
        sheetId: params.sheetId,
        manualRowIds: params.manualRowIds,
        baseVersion: params.baseVersion,
      }),
    resolveCellRawInput: (_sheetId, cellId) =>
      findLoadedCellById(cellId)?.raw_input ?? "",
  };

  // Replay an undo (entry.inverse) or redo (entry.forward) against the API,
  // threading the tracked workbook version through as base_version. A 409 means
  // the workbook moved on under us; any other failure is a real error. Either
  // way the history is now inconsistent, so reset it and refresh the workbook.
  const applyHistoryEntry = async (
    ops: Parameters<typeof replayOps>[0],
    failureTitle: string,
  ) => {
    history.setApplying(true);
    try {
      const version = await replayOps(
        ops,
        replayMutations,
        workbookVersionRef.current,
      );
      recordWorkbookVersion(version);
    } catch (mutationError) {
      history.reset();
      queryClient.invalidateQueries({
        queryKey: aiScriptSpreadsheetKey(businessId, runId),
      });
      if (activeSheet) {
        queryClient.invalidateQueries({
          queryKey: aiScriptSpreadsheetSheetKey(
            businessId,
            runId,
            activeSheet.id,
          ),
        });
      }

      if (isAiScriptWorkbookVersionConflictError(mutationError)) {
        toast.error("Couldn't apply undo/redo", {
          description:
            "The Grid changed since that edit, so it was refreshed instead. Review the current values and try again.",
        });
      } else {
        handleMutationError(failureTitle, mutationError);
      }
    } finally {
      history.setApplying(false);
      applyingRef.current = false;
    }
  };

  const isHistoryBlocked =
    isLocked || history.isApplying || workbookOperation.isPending;

  const undo = useCallback(() => {
    if (!businessId || applyingRef.current || isHistoryBlocked) return;
    applyingRef.current = true;
    void flushWorkbookCellEdits(businessId, runId)
      .then(() => {
        const entry = history.beginUndo();
        if (!entry) {
          applyingRef.current = false;
          return;
        }
        void applyHistoryEntry(entry.inverse, "Failed to undo");
      })
      .catch(() => {
        applyingRef.current = false;
      });
    // applyHistoryEntry / replayMutations are re-created each render but close
    // over refs + stable mutateAsync fns, so they need not be deps here.
  }, [businessId, history, isHistoryBlocked, runId]);

  const redo = useCallback(() => {
    if (!businessId || applyingRef.current || isHistoryBlocked) return;
    applyingRef.current = true;
    void flushWorkbookCellEdits(businessId, runId)
      .then(() => {
        const entry = history.beginRedo();
        if (!entry) {
          applyingRef.current = false;
          return;
        }
        void applyHistoryEntry(entry.forward, "Failed to redo");
      })
      .catch(() => {
        applyingRef.current = false;
      });
  }, [businessId, history, isHistoryBlocked, runId]);

  // Cmd/Ctrl+Z undo, Cmd/Ctrl+Shift+Z (or Ctrl+Y) redo, scoped to the workbook
  // surface. Skip when a text editor is focused — the formula-bar input or the
  // grid's open cell-edit overlay — so the browser's native text undo runs there.
  useEffect(() => {
    const handleUndoRedoShortcut = (event: KeyboardEvent) => {
      const action = matchesUndoRedoShortcut(event);
      if (!action) return;
      if (event.defaultPrevented || !isViewerShortcutScopeActive()) return;

      const active = document.activeElement;
      if (
        active instanceof HTMLElement &&
        (active.tagName === "INPUT" ||
          active.tagName === "TEXTAREA" ||
          active.isContentEditable)
      ) {
        return;
      }

      event.preventDefault();
      if (action === "undo") {
        undo();
      } else {
        redo();
      }
    };

    document.addEventListener("keydown", handleUndoRedoShortcut);
    return () =>
      document.removeEventListener("keydown", handleUndoRedoShortcut);
  }, [isViewerShortcutScopeActive, undo, redo]);

  useEffect(() => {
    if (!searchNavigationTarget) return;

    clearGridViewport();
    const matchingCell: Item = [
      searchNavigationTarget.col,
      searchNavigationTarget.row,
    ];
    setGridSelection({
      ...emptySelection,
      current: {
        cell: matchingCell,
        range: {
          x: matchingCell[0],
          y: matchingCell[1],
          width: 1,
          height: 1,
        },
        rangeStack: [],
      },
    });
    gridRef.current?.scrollTo(
      searchNavigationTarget.col,
      searchNavigationTarget.row,
      "both",
      40,
      40,
      { hAlign: "center", vAlign: "center" },
    );
  }, [clearGridViewport, searchNavigationTarget]);

  const bufferManualRowEdit = (
    cell: IAiSpreadsheetCell,
    edit: Omit<IAiSpreadsheetPasteEdit, "cell_id">,
  ): boolean => {
    if (!isOptimisticManualCell(cell) || !activeSheet) return false;

    const token = Array.from(bufferedManualRowEditsRef.current.keys()).find(
      (candidate) => optimisticManualCellBelongsTo(cell, candidate),
    );
    if (!token) return false;

    bufferedManualRowEditsRef.current
      .get(token)
      ?.set(cell.col, { col: cell.col, ...edit });

    const sheetKey = aiScriptSpreadsheetSheetKey(
      businessId,
      runId,
      activeSheet.id,
    );
    queryClient.setQueryData<IAiSpreadsheetSheetResponse>(sheetKey, (data) =>
      patchSpreadsheetSheetCells(data, [
        buildOptimisticCell(cell, edit.raw_input, edit.value_type, edit.kind),
      ]),
    );
    return true;
  };

  const bufferOptimisticPasteEdits = (
    edits: IAiSpreadsheetPasteEdit[],
  ): IAiSpreadsheetPasteEdit[] => {
    if (!activeSheet) return edits;

    const sheetData = queryClient.getQueryData<IAiSpreadsheetSheetResponse>(
      aiScriptSpreadsheetSheetKey(businessId, runId, activeSheet.id),
    );

    return edits.filter((edit) => {
      const cell = findSheetResponseCell(sheetData, edit.cell_id);
      if (!cell || !isOptimisticManualCell(cell)) return true;

      const { cell_id: _cellId, ...bufferedEdit } = edit;
      bufferManualRowEdit(cell, bufferedEdit);
      return false;
    });
  };

  const flushBufferedManualRowEdits = async ({
    token,
    sheetId,
    manualRowId,
  }: {
    token: string;
    sheetId: string;
    manualRowId: string | undefined;
  }) => {
    const buffered = Array.from(
      bufferedManualRowEditsRef.current.get(token)?.values() ?? [],
    );
    bufferedManualRowEditsRef.current.delete(token);
    if (buffered.length === 0) return;

    const sheetKey = aiScriptSpreadsheetSheetKey(businessId, runId, sheetId);
    if (!manualRowId) {
      void queryClient.invalidateQueries({ queryKey: sheetKey });
      toast.error("Couldn't save edits to the new row", {
        description:
          "The row was added, but its pending cell edits could not be matched. Please try them again.",
      });
      return;
    }

    let sheetData =
      queryClient.getQueryData<IAiSpreadsheetSheetResponse>(sheetKey);
    if (
      !sheetData?.sheet.cells.some((cell) => cell.manual_row_id === manualRowId)
    ) {
      await queryClient.refetchQueries({ queryKey: sheetKey, type: "active" });
      sheetData =
        queryClient.getQueryData<IAiSpreadsheetSheetResponse>(sheetKey);
    }
    if (!sheetData) {
      toast.error("Couldn't save edits to the new row", {
        description:
          "The row was added, but its pending cell edits could not be matched. Please try them again.",
      });
      return;
    }

    const edits = resolveBufferedManualRowEdits({
      cells: sheetData.sheet.cells,
      manualRowId,
      edits: buffered,
    });
    if (edits.length !== buffered.length) {
      void queryClient.invalidateQueries({ queryKey: sheetKey });
      toast.error("Couldn't save edits to the new row", {
        description:
          "The row was added, but one or more pending cell edits could not be matched. Please try them again.",
      });
      return;
    }

    const cellEdits = buildHistoryCellEdits(sheetData, edits);
    try {
      const response = await bulkPasteAsync({
        runId,
        sheetId,
        edits,
        rows: [],
      });
      recordWorkbookVersion(response.version);
      history.push(
        buildPasteEntry({ sheetId, cellEdits, label: "Edit new row" }),
      );
    } catch (mutationError) {
      handleMutationError("Failed to update the new row", mutationError);
    }
  };

  const commitRawInput = (cellId: string, rawInput: string) => {
    if (!activeSheet) return;

    // Read the prior value from the react-query cache (the source of truth) rather
    // than React state, and patch the cache synchronously below. The hook's
    // optimistic patch is async (it awaits cancelQueries) and React state only
    // updates on re-render, so a second edit fired before either lands would
    // otherwise capture the SAME prior — collapsing two rapid edits into one
    // undoable step. Chaining off the cache keeps each edit its own entry.
    const sheetKey = aiScriptSpreadsheetSheetKey(
      businessId,
      runId,
      activeSheet.id,
    );
    const cachedSheet =
      queryClient.getQueryData<IAiSpreadsheetSheetResponse>(sheetKey);
    const priorCell =
      findSheetResponseCell(cachedSheet, cellId) ?? findLoadedCellById(cellId);
    const nextKind = getRawInputKind(rawInput);

    if (
      priorCell &&
      bufferManualRowEdit(priorCell, { raw_input: rawInput, kind: nextKind })
    ) {
      return;
    }

    const priorKind = getCellKind(priorCell);
    const wasFormula = priorCell ? isFormulaCell(priorCell) : false;

    // Capture the prior input, but only PUSH the entry once the edit persists
    // (onSuccess) — a failed/rolled-back mutation must not strand an undo entry.
    const pendingEntry: WorkbookHistoryEntry | undefined = priorCell
      ? buildCellEditEntry({
          sheetId: activeSheet.id,
          cellId,
          coordinate: coordinateForCell(priorCell),
          priorRawInput: priorCell.raw_input ?? "",
          nextRawInput: rawInput,
          priorKind,
          nextKind,
        })
      : undefined;

    // Reflect this edit in the cache NOW so a rapid next edit reads it as its
    // prior (the hook re-applies the same optimistic patch idempotently).
    if (priorCell) {
      queryClient.setQueryData<IAiSpreadsheetSheetResponse>(sheetKey, (data) =>
        patchSpreadsheetSheetCells(data, [
          buildOptimisticCell(priorCell, rawInput, undefined, nextKind),
        ]),
      );
    }

    void updateCellAsync({
      runId,
      sheetId: activeSheet.id,
      cellId,
      rawInput,
      kind: nextKind,
    })
      .then((response) => {
        recordWorkbookVersion(response.operation.version);
        if (pendingEntry) history.push(pendingEntry);
        const refreshedCell = findSheetResponseCell(
          queryClient.getQueryData<IAiSpreadsheetSheetResponse>(sheetKey),
          cellId,
        );
        const notification = getCellEditNotification({
          cell: refreshedCell,
          wasFormula,
        });
        const feedback = cellEditFeedbackCollectorRef.current.completeSuccess(
          response.batch,
          notification,
        );
        if (feedback) {
          toast.info(feedback.title, {
            description: feedback.description,
            id: feedback.id,
          });
        }
      })
      .catch((mutationError: unknown) => {
        const feedback = cellEditFeedbackCollectorRef.current.completeError(
          mutationError,
          "Failed to update cell",
        );
        if (feedback) showMutationErrorFeedback(feedback);
      });
  };

  // Re-format a cell from the "Format cell" menu: re-send its current raw_input
  // under a new value_type (the type change coerces the value + records on the
  // override server-side). The grid re-renders by value_type.
  const formatCell = (
    cellRecord: IAiSpreadsheetCell,
    valueType: AiSpreadsheetValueType,
  ) => {
    if (!activeSheet || !canMutateActiveSheet || cellRecord.locked) return;

    const priorValueType = getCellFormatType(cellRecord);
    const pendingEntry: WorkbookHistoryEntry | undefined =
      priorValueType !== valueType
        ? buildFormatEditEntry({
            sheetId: activeSheet.id,
            cellId: cellRecord.id,
            coordinate: coordinateForCell(cellRecord),
            priorValueType,
            nextValueType: valueType,
          })
        : undefined;

    void directUpdateCellAsync({
      runId,
      sheetId: activeSheet.id,
      cellId: cellRecord.id,
      rawInput: cellRecord.raw_input ?? "",
      valueType,
    })
      .then((response) => {
        recordWorkbookVersion(response.version);
        if (pendingEntry) history.push(pendingEntry);
      })
      .catch((mutationError: unknown) =>
        handleMutationError("Failed to format cell", mutationError),
      );
  };

  const commitCellEdit = (cell: Item, newValue: EditableGridCell) => {
    if (!adaptedSheet || !canMutateActiveSheet) return;

    const cellRecord = adaptedSheet.getCellRecord(cell[0], cell[1]);

    if (!cellRecord) {
      // Typing into the blank row directly under the data adds a row, seeded
      // with what was typed (like a spreadsheet — no button needed).
      if (adaptedSheet.isAddRowCell(cell[0], cell[1])) {
        const typed = getEditedRawInput(newValue);
        if (typed.trim() !== "") {
          appendManualRow({ col: cell[0] + 1, value: typed });
        }
        return;
      }
      toast.error("Only existing Grid cells can be edited right now.");
      return;
    }

    // Read-only cell (for example, a locked label). Silently ignore so a pasted
    // block spanning it doesn't error.
    if (!isCellEditable(cellRecord)) return;

    const rawInput = getEditedRawInput(newValue);

    if (rawInput === getInCellEditValue(cellRecord)) return;

    commitRawInput(cellRecord.id, rawInput);
  };

  // Non-paste multi-cell edits (a single in-grid overlay commit, or a drag-fill).
  // glide funnels these through onCellsEdited and skips onCellEdited when this
  // returns true, so we forward each to commitCellEdit ourselves: cells with a
  // record are edited, and a cell on the blank row adds a row. Pastes are handled
  // wholesale in handlePaste (below) so they don't fragment here.
  const handleCellsEdited = (edits: readonly EditListItem[]) => {
    if (!adaptedSheet || !canMutateActiveSheet) return true;

    // Coalesce edits that land on the blank "add" row into ONE new row — a
    // drag-fill spanning N columns must add a single row with N cells, not N
    // one-cell rows. Existing-cell edits commit individually.
    const addRowSeeds: { col: number; value: string }[] = [];
    edits.forEach(({ location, value }) => {
      if (adaptedSheet.getCellRecord(location[0], location[1])) {
        commitCellEdit(location, value);
      } else if (adaptedSheet.isAddRowCell(location[0], location[1])) {
        const typed = getEditedRawInput(value);
        if (typed.trim() !== "") {
          addRowSeeds.push({ col: location[0] + 1, value: typed });
        }
      }
    });
    if (addRowSeeds.length > 0) appendManualRow(addRowSeeds);
    return true;
  };

  // Own the entire paste (return false so glide doesn't also write cells). Lines
  // landing on existing rows edit those cells; lines past the last data row
  // become new rows — one row per line, in one batch. This keeps a paste that
  // straddles the data boundary from fragmenting into single-cell rows or
  // dropping its overflow onto read-only empty cells.
  const handlePaste = (
    target: Item,
    values: readonly (readonly string[])[],
  ) => {
    const pasteMetadata = consumePasteMetadata();
    if (!adaptedSheet || !canMutateActiveSheet || !activeSheet) return true;

    // Excel's plain-text clipboard data is the reliable rectangle. Its HTML
    // still supplies source types, while internal Volca copies keep Glide's
    // values so private formula metadata follows the existing path.
    const pasteValues = pasteMetadata.externalValues ?? values;

    const startRow = target[1] + 1; // 1-based
    const startCol = target[0] + 1;
    // Under a region view a paste can only overwrite rows that are on screen:
    // "the row after the last one displayed" has no saved position to create, and
    // guessing one would append a row the active filter then hides. Trim to what
    // is visible so the in-view rows still paste through to their saved cells.
    const pastedValues = hasActiveRegionView
      ? pasteValues.slice(0, Math.max(0, maxDisplayRow - target[1]))
      : pasteValues;
    if (pastedValues.length === 0) return false;

    const payload = buildBulkPastePayload({
      values: pastedValues,
      startRow,
      startCol,
      maxContentRow: maxDisplayRow,
      getCellRecord: adaptedSheet.getCellRecord,
      cellTypes: pasteMetadata.cellTypes,
      copiedRange: pasteMetadata.copiedRange,
    });
    if (!payload) return false;

    const persistedPayload = {
      ...payload,
      edits: bufferOptimisticPasteEdits(payload.edits),
    };
    if (
      persistedPayload.edits.length === 0 &&
      persistedPayload.rows.length === 0
    ) {
      return false;
    }

    // A paste is one atomic undo unit: the whole rectangle applies in one bulk
    // request. Capture each edited existing cell's prior value from the cache
    // (before the paste), then push a single history entry once the created rows
    // have server ids.
    const sheetId = activeSheet.id;
    const sheetData = queryClient.getQueryData<IAiSpreadsheetSheetResponse>(
      aiScriptSpreadsheetSheetKey(businessId, runId, sheetId),
    );
    const cellEdits = buildHistoryCellEdits(sheetData, persistedPayload.edits);

    void bulkPasteAsync({ runId, sheetId, ...persistedPayload })
      .then((response) => {
        recordWorkbookVersion(response.version);
        history.push(
          buildPasteEntry({
            sheetId,
            cellEdits,
            createdRowIds: response.manual_row_ids ?? [],
          }),
        );
      })
      .catch((mutationError: unknown) =>
        handleMutationError("Failed to paste cells", mutationError),
      );
    return false;
  };

  // Formula fills use the same atomic endpoint and history shape as paste: one
  // request recalculates the workbook, and one undo restores every overwritten
  // destination. Formula translation/pattern selection stays in a pure helper so
  // mouse and keyboard fills share exactly the same behavior.
  const commitFormulaFill = (
    { patternSource, fillDestination }: FormulaFillRequest,
    label: string,
  ): void => {
    if (!adaptedSheet || !canMutateActiveSheet || !activeSheet) return;

    const fillEdits = buildFormulaFillEdits({
      patternSource,
      fillDestination,
      getCellRecord: adaptedSheet.getCellRecord,
    });
    if (fillEdits.length === 0) return;

    const persistedEdits = bufferOptimisticPasteEdits(fillEdits);
    if (persistedEdits.length === 0) return;

    const sheetId = activeSheet.id;
    const sheetData = queryClient.getQueryData<IAiSpreadsheetSheetResponse>(
      aiScriptSpreadsheetSheetKey(businessId, runId, sheetId),
    );
    const cellEdits = buildHistoryCellEdits(sheetData, persistedEdits);

    void bulkPasteAsync({ runId, sheetId, edits: persistedEdits, rows: [] })
      .then((response) => {
        recordWorkbookVersion(response.version);
        history.push(buildPasteEntry({ sheetId, cellEdits, label }));
      })
      .catch((mutationError: unknown) =>
        handleMutationError("Failed to fill formulas", mutationError),
      );
  };

  const { consumeFillCancellation } = useFormulaFillInteractions({
    enabled: canMutateActiveSheet,
    getCellRecord: adaptedSheet?.getCellRecord,
    gridSelection,
    gridWrapperRef,
    onFill: commitFormulaFill,
  });

  const handleFormulaFillPattern = (event: FillPatternEventArgs) => {
    // Glide's default repeats display values verbatim. Own the event so formulas
    // are translated relative to their actual source cell instead.
    event.preventDefault();
    if (consumeFillCancellation()) return;
    commitFormulaFill(event, "Fill formulas");
  };

  // Own Backspace/Delete over a selection (a whole row, column, or range) so it
  // clears every editable cell through ONE bulk request + recalculation, instead
  // of glide's default which fires an empty edit per cell. Returning false stops
  // glide from also clearing them.
  const handleDelete = (selection: GridSelection): boolean => {
    if (!adaptedSheet || !canMutateActiveSheet || !activeSheet) return true;

    const payload = buildBulkClearPayload({
      selection,
      maxContentRow: maxDisplayRow,
      maxContentCol,
      getCellRecord: adaptedSheet.getCellRecord,
    });
    if (!payload) return false;

    const persistedEdits = bufferOptimisticPasteEdits(payload.edits);

    // Clearing a single cell goes through the lighter single-cell endpoint (the
    // same path as a single-cell edit, which records its own undo entry).
    if (persistedEdits.length === 0) return false;
    if (persistedEdits.length === 1) {
      commitRawInput(persistedEdits[0].cell_id, persistedEdits[0].raw_input);
      return false;
    }

    // A multi-cell clear is one atomic undo unit too.
    const sheetId = activeSheet.id;
    const sheetData = queryClient.getQueryData<IAiSpreadsheetSheetResponse>(
      aiScriptSpreadsheetSheetKey(businessId, runId, sheetId),
    );
    const cellEdits = buildHistoryCellEdits(sheetData, persistedEdits);

    void bulkPasteAsync({ runId, sheetId, edits: persistedEdits, rows: [] })
      .then((response) => {
        recordWorkbookVersion(response.version);
        history.push(buildPasteEntry({ sheetId, cellEdits }));
      })
      .catch((mutationError: unknown) =>
        handleMutationError("Failed to clear cells", mutationError),
      );
    return false;
  };

  // Select + scroll a (0-based) grid row into view so the user can type into it.
  const focusGridRow = (rowIndex: number, colIndex = 0) => {
    clearGridViewport();
    const cell: Item = [colIndex, rowIndex];
    setGridSelection({
      ...emptySelection,
      current: {
        cell,
        range: { x: colIndex, y: rowIndex, width: 1, height: 1 },
        rangeStack: [],
      },
    });
    requestAnimationFrame(() =>
      gridRef.current?.scrollTo(colIndex, rowIndex, "both", 40, 40, {
        hAlign: "center",
        vAlign: "center",
      }),
    );
  };

  // Append a blank row across the content columns, optionally seeding one cell
  // with a typed value, then select + scroll to it so the user keeps typing.
  // Shared by typing into the blank row, the toolbar "Add row" button, and the
  // grid's trailing "+".
  const appendManualRow = (
    seed?: { col: number; value: string } | { col: number; value: string }[],
  ) => {
    if (!canMutateActiveSheet || !activeSheet) return;

    const newRowIndex = maxContentRow; // 0-based row of the appended row
    const firstSeed = Array.isArray(seed) ? seed[0] : seed;
    const focusCol = firstSeed ? firstSeed.col - 1 : 0;
    const sheetId = activeSheet.id;
    const cells = buildManualRowCells(maxContentCol, seed);
    const optimisticToken = crypto.randomUUID();
    bufferedManualRowEditsRef.current.set(optimisticToken, new Map());

    createManualRow(
      { runId, sheetId, cells, optimisticToken },
      {
        onSuccess: (response) => {
          recordWorkbookVersion(response.version);
          history.push(
            buildManualRowCreateEntry({
              sheetId,
              rows: [{ cells }],
              manualRowIds: response.manual_row_ids ?? [],
            }),
          );
          void flushBufferedManualRowEdits({
            token: optimisticToken,
            sheetId,
            manualRowId: response.manual_row_ids?.[0],
          });
          focusGridRow(newRowIndex, focusCol);
        },
        onError: (mutationError: unknown) => {
          bufferedManualRowEditsRef.current.delete(optimisticToken);
          handleMutationError("Failed to add row", mutationError);
        },
      },
    );
  };

  // Insert a blank row via a positioned anchor: the server opens a gap after the
  // 1-based `afterRow` (shifting rows down) and stores a rerun-safe signature.
  // The inserted row lands at 1-based `afterRow + 1`, i.e. 0-based `afterRow`.
  const insertManualRow = (afterRow: number) => {
    if (!canMutateActiveSheet || !activeSheet) return;

    const sheetId = activeSheet.id;
    const cells = buildManualRowCells(maxContentCol);
    const anchor = { after_row: afterRow };
    const optimisticToken = crypto.randomUUID();
    bufferedManualRowEditsRef.current.set(optimisticToken, new Map());

    createManualRow(
      { runId, sheetId, cells, anchor, optimisticToken },
      {
        onSuccess: (response) => {
          recordWorkbookVersion(response.version);
          history.push(
            buildManualRowCreateEntry({
              sheetId,
              rows: [{ cells, anchor }],
              manualRowIds: response.manual_row_ids ?? [],
              label: "Insert row",
            }),
          );
          void flushBufferedManualRowEdits({
            token: optimisticToken,
            sheetId,
            manualRowId: response.manual_row_ids?.[0],
          });
          focusGridRow(afterRow);
        },
        onError: (mutationError: unknown) => {
          bufferedManualRowEditsRef.current.delete(optimisticToken);
          handleMutationError("Failed to insert row", mutationError);
        },
      },
    );
  };

  const getCellQuestionContext = (col: number, row: number) => {
    if (!onAskAiAboutCell || !adaptedSheet || !activeLoadedSheet) return null;
    return buildAiScriptChatCellQuestion({
      adaptedSheet,
      activeSheet: activeLoadedSheet,
      cell: [col, row],
      getSavedRow: (displayRow) =>
        getSavedSpreadsheetRow(regionRowMap, displayRow),
    });
  };

  // Right-click on an existing content row (not the trailing blank "add" row)
  // opens the cell menu at the cursor: row/format actions on editable sheets,
  // plus comments and AI questions wherever the parent enables them.
  const handleCellContextMenu = (cell: Item, event: CellClickedEventArgs) => {
    const gridRow = cell[1];
    if (
      gridRow < 0 ||
      gridRow >= maxDisplayRow ||
      (!canMutateActiveSheet &&
        !areCommentsEnabled &&
        !getCellQuestionContext(cell[0], gridRow))
    ) {
      return;
    }
    event.preventDefault();
    setColumnHeaderMenu(null);
    setRowMenu({
      x: lastContextPointerRef.current.x,
      y: lastContextPointerRef.current.y,
      gridRow,
      col: cell[0],
    });
  };

  const handleCellClicked = (cell: Item, event: CellClickedEventArgs) => {
    if (handleMultiSelectCellClick(cell, event)) return;

    // Clicking an already-selected commented cell reopens its card (the
    // selection does not change, so the selection effect cannot).
    if (areCommentsEnabled && cell[1] >= 0) {
      const saved = savedCellCoordinate(cell[0], cell[1]);
      if (commentIndex.hasOpenThreadAt(saved.row, saved.col)) {
        openCommentCard(cell[0], cell[1], false);
      }
    }

    openSpreadsheetColumnMenu(cell, {
      x: lastContextPointerRef.current.x,
      y: lastContextPointerRef.current.y,
    });
  };

  const changeActiveRegionView = (
    regionId: string,
    state: ReturnType<typeof emptySpreadsheetRegionViewState>,
  ) => {
    setRegionViewStatesBySheet((current) => ({
      ...current,
      [activeSheetValue]: {
        ...(current[activeSheetValue] ?? {}),
        [regionId]: state,
      },
    }));
    setGridSelection(emptySelection);
  };

  const clearActiveRegionViews = () => {
    setRegionViewStatesBySheet((current) => ({
      ...current,
      [activeSheetValue]: {},
    }));
    setSpreadsheetColumnMenu(null);
    setGridSelection(emptySelection);
  };

  const createRegion = async (region: SpreadsheetRegionDraft) => {
    if (!activeLoadedSheet || !canMutateActiveSheet) return;
    try {
      await createSpreadsheetRegion.mutateAsync({
        runId,
        sheetId: activeLoadedSheet.id,
        region,
      });
    } catch (mutationError) {
      handleMutationError("Failed to create table", mutationError);
      throw mutationError;
    }
  };

  const updateRegion = async (
    regionId: string,
    region: SpreadsheetRegionDraft,
  ) => {
    if (!activeLoadedSheet || !canMutateActiveSheet) return;
    try {
      await updateSpreadsheetRegion.mutateAsync({
        runId,
        sheetId: activeLoadedSheet.id,
        regionId,
        region,
      });
      setRegionViewStatesBySheet((current) => ({
        ...current,
        [activeSheetValue]: {
          ...(current[activeSheetValue] ?? {}),
          [regionId]: emptySpreadsheetRegionViewState(),
        },
      }));
    } catch (mutationError) {
      handleMutationError("Failed to update table", mutationError);
      throw mutationError;
    }
  };

  const deleteRegion = async (regionId: string) => {
    if (!activeLoadedSheet || !canMutateActiveSheet) return;
    try {
      await deleteSpreadsheetRegion.mutateAsync({
        runId,
        sheetId: activeLoadedSheet.id,
        regionId,
      });
      setRegionViewStatesBySheet((current) => ({
        ...current,
        [activeSheetValue]: Object.fromEntries(
          Object.entries(current[activeSheetValue] ?? {}).filter(
            ([id]) => id !== regionId,
          ),
        ),
      }));
    } catch (mutationError) {
      handleMutationError("Failed to remove table", mutationError);
      throw mutationError;
    }
  };

  const confirmRegionOverride = async (
    review: IAiSpreadsheetRegionOverrideReview,
    candidate: IAiSpreadsheetRegionOverrideCandidate,
  ) => {
    if (!activeLoadedSheet) return;
    try {
      await confirmSpreadsheetRegionOverride.mutateAsync({
        runId,
        sheetId: activeLoadedSheet.id,
        overrideId: review.id,
        candidate,
      });
      clearActiveRegionViews();
    } catch (mutationError) {
      handleMutationError("Failed to confirm saved table", mutationError);
    }
  };

  const canChangeColumnValueType =
    pendingWorkbookMutations === 0 &&
    canMutateActiveSheet &&
    !isHistoryBlocked &&
    !isUpdatingAnyCell &&
    !isBulkPasting &&
    !isCreatingManualRow &&
    !isUpdatingColumnValueType &&
    !sheetLayout.isSaving;

  const changeColumnValueType = async (
    col: number,
    valueType: AiSpreadsheetValueType,
  ) => {
    if (!activeSheet || !canChangeColumnValueType) return;
    try {
      const result = await updateColumnValueTypeAsync({
        runId,
        sheetId: activeSheet.id,
        col,
        valueType,
        baseVersion: workbookVersionRef.current,
      });
      recordWorkbookVersion(result.version);
      // A column operation can touch more cells than the local history knows.
      history.reset();
      toast.success("Column value type updated");
    } catch (mutationError) {
      handleMutationError("Failed to change column value type", mutationError);
    }
  };

  const handleHeaderContextMenu = (
    col: number,
    event: HeaderClickedEventArgs,
  ) => {
    if (!canMutateActiveSheet) return;
    event.preventDefault();
    setRowMenu(null);
    setColumnHeaderMenu({
      x: lastContextPointerRef.current.x,
      y: lastContextPointerRef.current.y,
      columnIndex: col,
    });
  };

  const handleRowAppended = () => appendManualRow();

  const handleFormulaBarCommit = (
    rawInput: string,
    target: FormulaBarCommitTarget,
  ) => {
    if (!canMutateActiveSheet) return;

    // Typing a value for the blank row adds a row.
    if (target.kind === "new-row") {
      if (rawInput.trim() !== "") {
        appendManualRow({ col: target.columnIndex + 1, value: rawInput });
      }
      return;
    }

    const targetCell = findLoadedCellById(target.cellId);
    if (!targetCell || !isCellEditable(targetCell)) return;
    commitRawInput(target.cellId, rawInput);
  };

  const rowResize = useRowResize({
    scope: sheetLayout.scope,
    sheetId: activeLoadedSheet?.id ?? null,
    rowMap: regionRowMap,
    rowCount: adaptedSheet?.rowCount ?? 0,
    gridRef,
    getRowHeight: adaptedSheet?.getRowHeight,
    onResizeEnd: sheetLayout.onRowResizeEnd,
  });

  const handleSheetChange = (value: string) => {
    changeActiveSheet(value);
    // Each queued edit keeps its source sheet id. Let the user move to the next
    // sheet now while the old sheet's edits drain in the background.
    if (businessId) void flushWorkbookCellEdits(businessId, runId);
  };

  if (isLoading) return <SheetLoadingState fillHeight={fillHeight} />;

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTitle>{getErrorTitle(error)}</AlertTitle>
        <AlertDescription>
          {isAiScriptWorkbookAuthorizationError(error)
            ? getAiScriptWorkbookAuthorizationMessage(error)
            : error instanceof Error
              ? error.message
              : "Please try again or download the Grid instead."}
        </AlertDescription>
      </Alert>
    );
  }

  if (!spreadsheet || sheets.length === 0) {
    return (
      <EmptySheetState
        title="No visible Grid sheets"
        description="You do not currently have access to any sheets in this Grid."
        fillHeight={fillHeight}
      />
    );
  }

  if (!activeSheet) {
    return (
      <EmptySheetState
        title="No sheet selected"
        description="Choose an available sheet to view its cells."
        fillHeight={fillHeight}
      />
    );
  }

  const rowMenuQuestion = rowMenu
    ? getCellQuestionContext(rowMenu.col, rowMenu.gridRow)
    : null;

  // The cell the right-click menu targets, for the "Format cell" submenu. Null
  // when the click was on the row-number marker (no column cell).
  const rowMenuCellRecord =
    rowMenu && adaptedSheet
      ? adaptedSheet.getCellRecord(rowMenu.col, rowMenu.gridRow)
      : undefined;

  // Any manual-row cell in the targeted row, so "Delete row" works from a
  // right-click on the row-number marker too (which has no column cell), not
  // just from a cell in the row.
  // Whether the right-clicked cell already belongs to a saved table, so the menu
  // can offer "Edit" rather than "Create".
  const rowMenuRegion =
    rowMenu && rowMenuCellRecord
      ? activeRegions.find(
          (region) =>
            rowMenuCellRecord.row >= region.header_row &&
            rowMenuCellRecord.row <= region.record_end_row &&
            rowMenuCellRecord.col >= region.start_col &&
            rowMenuCellRecord.col <= region.end_col,
        )
      : undefined;

  const rowMenuManualCell =
    rowMenu && adaptedSheet
      ? adaptedSheet.getManualRowCell(rowMenu.gridRow)
      : undefined;

  return (
    <Tabs
      ref={viewerRef}
      value={activeSheetValue}
      onValueChange={handleSheetChange}
      className={cn("w-full min-w-0", {
        "h-full": fillHeight && !isMaximized,
      })}
    >
      <Stack
        fullWidth
        gap="none"
        // When maximized with a sidebar open, reserve its exact width on the
        // right so the two sit side-by-side (no overlap); otherwise the overlay
        // is full-width (right-0). Inline style keeps the reserved width tied to
        // the single sidebar-width source. `width: auto` is required so the
        // left/right pair actually sizes the box — the `fullWidth` (w-full)
        // class would otherwise force width:100% and make `right` a no-op, so
        // the grid would stay full width and hide its right columns behind the
        // sidebar.
        style={
          isMaximized && sidebarOpen
            ? { right: "var(--simple-sidebar-width)", width: "auto" }
            : undefined
        }
        className={cn(
          "min-w-0 overflow-hidden border border-stroke-tertiary bg-surface-primary",
          {
            "fixed bottom-0 left-0 top-0 z-[900] h-screen rounded-none":
              isMaximized,
            "right-0": isMaximized && !sidebarOpen,
            "rounded-lg": !isMaximized,
            "h-full": fillHeight && !isMaximized,
          },
        )}
      >
        {!isTooNarrowForGrid && (
          <FormulaBar
            coordinate={selectedCoordinate}
            commitTarget={formulaBarCommitTarget}
            value={formulaBarValue}
            errorCode={selectedCellErrorCode}
            canEdit={!!formulaBarCommitTarget}
            addRowMode={canMutateActiveSheet && isNewRowSelected}
            isSheetReadOnly={!isActiveSheetEditable}
            isCellLocked={selectedCellRecord?.locked === true}
            isRecalculating={
              workbookOperation.isPending ||
              isCreatingManualRow ||
              isUpdatingAnyCell ||
              isBulkPasting ||
              isUpdatingColumnValueType
            }
            operationError={workbookOperation.errorMessage}
            onRetryOperation={workbookOperation.retry ?? undefined}
            canUndo={history.canUndo && !isHistoryBlocked}
            canRedo={history.canRedo && !isHistoryBlocked}
            undoLabel={history.undoLabel}
            redoLabel={history.redoLabel}
            isUndoRedoApplying={history.isApplying}
            onUndo={undo}
            onRedo={redo}
            valueType={
              selectedCellRecord ? getCellFormatType(selectedCellRecord) : null
            }
            onFormatChange={
              canMutateActiveSheet &&
              selectedCellRecord &&
              !selectedCellRecord.locked &&
              !isOptimisticManualCell(selectedCellRecord)
                ? (vt) => formatCell(selectedCellRecord, vt)
                : undefined
            }
            onCommit={handleFormulaBarCommit}
            isMaximized={isMaximized}
            onToggleMaximize={() => setIsMaximized((prev) => !prev)}
            onViewEdit={
              selectedEditedCell && onViewEditedCell
                ? () => onViewEditedCell(selectedEditedCell)
                : undefined
            }
            rightAccessory={
              <>
                {selectedColumnIndex !== undefined && (
                  <ColumnValueTypeControl
                    columnIndex={selectedColumnIndex}
                    disabled={!canChangeColumnValueType}
                    onValueTypeChange={(col, valueType) =>
                      void changeColumnValueType(col, valueType)
                    }
                  />
                )}
                {hasActiveRegionView && (
                  <Button
                    variant="text"
                    size="xs"
                    onClick={clearActiveRegionViews}
                  >
                    Clear filters and sorts
                  </Button>
                )}
                {activeSheetApproval && (
                  <SheetApprovalControl
                    accessKey={activeSheetApproval}
                    onApprovalChange={onSheetApprovalChange}
                    pendingApproval={pendingSheetApproval}
                    overlayClassName={VIEWER_OVERLAY_CLASSNAME}
                  />
                )}
                <SheetSearchControl {...sheetSearchControlProps} />
              </>
            }
          />
        )}

        <SpreadsheetRegionReviewBanner
          reviews={activeRegionOverrideReviews}
          canConfirm={canMutateActiveSheet}
          isConfirming={confirmSpreadsheetRegionOverride.isPending}
          onConfirm={confirmRegionOverride}
        />
        <SpreadsheetRegionWorkbookReviewBanner
          reviews={workbookRegionOverrideReviews}
        />

        <Stack
          fullWidth
          gap="none"
          className={cn({
            "min-h-0 min-w-0 flex-1": isMaximized || fillHeight,
            "h-[600px]": !isMaximized && !fillHeight,
          })}
        >
          {isTooNarrowForGrid ? (
            <EmptySheetState
              title="Open this on a bigger screen"
              description="The Grid needs more room than a phone can give it. Sign in on a desktop to read this workbook."
              isFramed={false}
            />
          ) : activeSheetError ? (
            <Stack fullWidth justify="center" className="h-full p-8">
              <Alert variant="destructive">
                <AlertTitle>Unable to load {activeSheet.name}</AlertTitle>
                <AlertDescription>
                  {activeSheetError instanceof Error
                    ? activeSheetError.message
                    : "This sheet is no longer available."}
                </AlertDescription>
              </Alert>
            </Stack>
          ) : isActiveSheetLoading ? (
            <LoadingSpinner
              aria-label={`Loading ${activeSheet.name}`}
              color="primary"
              size="lg"
            />
          ) : isActiveSheetEmpty ? (
            <EmptySheetState
              title={`${activeSheet.name} is empty`}
              isFramed={false}
            />
          ) : adaptedSheet ? (
            <div
              ref={gridWrapperRef}
              // Read the clipboard's text/html (Excel / Sheets types) before
              // glide handles the paste; consumed in handlePaste.
              onPasteCapture={handlePasteCapture}
              // Remember source coordinates for copies originating in this grid.
              // Paste translation also requires an exact clipboard-value match.
              onCopyCapture={handleCopyCapture}
              onScrollCapture={handleGridScrollCapture}
              // Let explicit user navigation supersede any short-lived restore
              // re-assertion still pending after a data refresh.
              onPointerDownCapture={(event) => {
                cancelViewportRestore();
                selectionBeforePointerDownRef.current = gridSelection;
                lastContextPointerRef.current = {
                  x: event.clientX,
                  y: event.clientY,
                };
                rowResize.onPointerDownCapture(event);
              }}
              onPointerMoveCapture={rowResize.onPointerMoveCapture}
              onPointerUpCapture={rowResize.onPointerUpCapture}
              onPointerCancelCapture={rowResize.onPointerCancelCapture}
              onLostPointerCapture={rowResize.onPointerCancelCapture}
              onPointerLeave={rowResize.onPointerLeave}
              onWheelCapture={cancelViewportRestore}
              onKeyDownCapture={cancelViewportRestore}
              // Capture the pointer position before Glide handles the
              // right-click, so the insert menu opens under the cursor.
              onContextMenuCapture={(event) => {
                lastContextPointerRef.current = {
                  x: event.clientX,
                  y: event.clientY,
                };
              }}
              className={cn(
                "quiet-scrollbar spreadsheet-overscroll-guard h-full min-h-0 w-full min-w-0",
                {
                  // Glide receives pointer events on its transparent scroller,
                  // above the canvas, and sets that layer's cursor inline.
                  "cursor-row-resize [&_.dvn-scroller]:!cursor-row-resize [&_canvas]:!cursor-row-resize":
                    rowResize.isResizingRow,
                },
              )}
            >
              <DataEditor
                ref={gridRef}
                columns={columns}
                rows={adaptedSheet.rowCount}
                getCellContent={([col, row]) =>
                  adaptedSheet.getCellContent(col, row)
                }
                getCellsForSelection={adaptedSheet.getCellsForSelection}
                customRenderers={CUSTOM_CELL_RENDERERS}
                onColumnResize={sheetLayout.onColumnResize}
                onColumnResizeEnd={sheetLayout.onColumnResizeEnd}
                rowHeight={rowResize.rowHeight}
                onVisibleRegionChanged={rowResize.onVisibleRegionChanged}
                width="100%"
                height="100%"
                // Pinned: Glide otherwise picks the gutter width from the row
                // count, so it visibly jumped whenever a filter changed how many
                // rows there were — and the saved-number overlay needs a width it
                // can rely on.
                rowMarkers={{
                  kind: "clickable-number",
                  width: SPREADSHEET_ROW_MARKER_WIDTH,
                  // The same grey the column-letter strip takes, so the two axes
                  // read as one frame around the data rather than one grey rail
                  // and one white one. The selected row still tints a step
                  // darker over this — see `drawSelectedRowMarkerBackground`.
                  theme: { bgCell: volcaColor("--surface-secondary") },
                }}
                theme={gridTheme}
                drawHeader={drawGridHeader}
                renderers={gridRenderers}
                headerIcons={sprites}
                imageWindowLoader={gridImageWindowLoader}
                freezeColumns={frozenColumn ?? 0}
                smoothScrollX
                smoothScrollY
                // Cmd/Ctrl+Enter is owned by formula fill while the grid has a
                // valid range instead of merely scrolling to the active cell.
                keybindings={{ scrollToSelectedCell: false }}
                // Multi-rect keeps click-drag range selection and lets Ctrl/Cmd
                // clicks add separate cells to the controlled selection.
                rangeSelect="multi-rect"
                onHeaderContextMenu={
                  canMutateActiveSheet ? handleHeaderContextMenu : undefined
                }
                columnSelect="single"
                rowSelect="single"
                highlightRegions={gridHighlightRegions}
                gridSelection={gridSelection}
                onGridSelectionChange={handleGridSelectionChange}
                onCellClicked={handleCellClicked}
                onKeyDown={handleGridKeyDown}
                onFinishedEditing={handleFinishedEditing}
                onCellEdited={commitCellEdit}
                onCellsEdited={handleCellsEdited}
                drawCell={drawCell}
                {...(canMutateActiveSheet
                  ? {
                      fillHandle: isFormulaSourceRange(
                        gridSelection.current?.range,
                        adaptedSheet.getCellRecord,
                      ),
                      allowedFillDirections: "orthogonal" as const,
                      onFillPattern: handleFormulaFillPattern,
                      onPaste: handlePaste,
                      onDelete: handleDelete,
                      onCellContextMenu: handleCellContextMenu,

                      // Appending writes a row at the end of the SAVED sheet, which
                      // an active filter would immediately hide. Offer it only on
                      // the unprojected view, where the new row lands where the
                      // user is looking.
                      ...(hasActiveRegionView
                        ? {}
                        : {
                            onRowAppended: handleRowAppended,
                            trailingRowOptions: {
                              sticky: false,
                              tint: true,
                            },
                          }),
                    }
                  : areCommentsEnabled || onAskAiAboutCell
                    ? { onCellContextMenu: handleCellContextMenu }
                    : {})}
              />
            </div>
          ) : (
            <EmptySheetState
              title={`${activeSheet.name} is unavailable`}
              description="Choose another sheet or refresh the Grid."
              isFramed={false}
            />
          )}
        </Stack>

        {!isTooNarrowForGrid && (
          <SheetTabList
            activeSheetId={activeSheetValue}
            isSheetSwitcherOpen={isSheetSwitcherOpen}
            sheets={sheets}
            onSheetSwitcherOpenChange={setIsSheetSwitcherOpen}
            onSheetSelect={handleSheetChange}
            onSheetPrefetchIntent={prefetchSheet}
            approvalStatus={approvalStatus}
            rightAccessory={
              selectionSummary ? (
                <SelectionSummaryControl summary={selectionSummary} />
              ) : null
            }
          />
        )}

        <RowInsertMenu
          target={rowMenu}
          onAskAi={
            rowMenuQuestion
              ? () => {
                  setGridSelection(rowMenuQuestion.selection);
                  onAskAiAboutCell?.(rowMenuQuestion.context);
                  setRowMenu(null);
                  setIsMaximized(false);
                }
              : undefined
          }
          canInsertRow={canMutateActiveSheet}
          commentActionLabel={
            areCommentsEnabled && rowMenu
              ? (() => {
                  const saved = savedCellCoordinate(
                    rowMenu.col,
                    rowMenu.gridRow,
                  );
                  return commentIndex.hasOpenThreadAt(saved.row, saved.col)
                    ? "Reply to comment"
                    : commentIndex.threadsAt(saved.row, saved.col).length > 0
                      ? "View comments"
                      : "Comment";
                })()
              : null
          }
          onCommentAction={() => {
            if (rowMenu) openCommentCard(rowMenu.col, rowMenu.gridRow, true);
            setRowMenu(null);
          }}
          canFormatCell={
            !!rowMenuCellRecord &&
            canMutateActiveSheet &&
            !rowMenuCellRecord.locked &&
            !isOptimisticManualCell(rowMenuCellRecord)
          }
          currentValueType={
            rowMenuCellRecord ? getCellFormatType(rowMenuCellRecord) : null
          }
          canDeleteRow={
            !!rowMenuManualCell?.manual_row_id &&
            canMutateActiveSheet &&
            !isOptimisticManualCell(rowMenuManualCell)
          }
          // Named for what the click will do: edit the table the cell already
          // belongs to, or define one over the current selection.
          tableActionLabel={
            canMutateActiveSheet
              ? rowMenuRegion
                ? "Edit table…"
                : "Create table from selection…"
              : null
          }
          isTableActionDisabled={hasActiveRegionView}
          onTableAction={() => {
            setRegionDialogTarget({
              sheetId: activeSheetValue,
              selection: gridSelection,
              existingRegion: rowMenuRegion,
            });
            setRowMenu(null);
          }}
          onClose={() => setRowMenu(null)}
          // Map the clicked display row once. "Below" is one row after that
          // saved anchor, not the next visible row under a filter or sort.
          onInsertRow={(displayRow, position) =>
            insertManualRow(
              getSavedRowInsertAnchor(regionRowMap, displayRow, position),
            )
          }
          onFormatCell={(valueType) => {
            if (rowMenuCellRecord) formatCell(rowMenuCellRecord, valueType);
          }}
          onDeleteRow={() => {
            const manualRowId = rowMenuManualCell?.manual_row_id;
            if (!activeSheet || !manualRowId) return;
            const sheetId = activeSheet.id;
            // Delete immediately on click, Excel-style — no confirm dialog. The
            // optimistic mutation drops the row from the grid while the request
            // remains in flight; settled queries refresh the recalculated totals.
            // Record the undo entry only on success so a failed/rolled-back delete
            // doesn't leave a phantom entry whose undo would restore a live row.
            deleteManualRow(
              { runId, sheetId, manualRowId },
              {
                onSuccess: (response) => {
                  recordWorkbookVersion(response.version);
                  history.push(
                    buildManualRowDeleteEntry({ sheetId, manualRowId }),
                  );
                },
                onError: (mutationError: unknown) =>
                  handleMutationError("Failed to delete row", mutationError),
              },
            );
          }}
        />
        {areCommentsEnabled && (
          <CellCommentCard
            businessId={businessId}
            runId={runId}
            target={commentCard}
            anchor={commentCardAnchor}
            threads={
              commentCard
                ? commentIndex.threadsAt(commentCard.row, commentCard.col)
                : []
            }
            onClose={() => setCommentCard(null)}
          />
        )}
        {activeLoadedSheet &&
          regionDialogTarget?.sheetId === activeLoadedSheet.id && (
            <SpreadsheetRegionControl
              sheet={activeLoadedSheet}
              selection={regionDialogTarget.selection}
              existingRegion={regionDialogTarget.existingRegion}
              onOpenChange={(open) => {
                if (!open) setRegionDialogTarget(null);
              }}
              isSaving={
                createSpreadsheetRegion.isPending ||
                updateSpreadsheetRegion.isPending ||
                deleteSpreadsheetRegion.isPending
              }
              onPreviewChange={setRegionPreview}
              onCreate={createRegion}
              onUpdate={updateRegion}
              onDelete={deleteRegion}
            />
          )}
        <ColumnHeaderMenu
          target={columnHeaderMenu}
          frozenColumn={frozenColumn}
          isSaving={!canMutateActiveSheet || sheetLayout.isSaving}
          onClose={() => setColumnHeaderMenu(null)}
          onFrozenColumnChange={(frozenColumn) => {
            setColumnHeaderMenu(null);
            sheetLayout.onFrozenColumnChange(frozenColumn);
          }}
        />
        <SpreadsheetColumnMenu
          target={spreadsheetColumnMenu}
          viewState={
            spreadsheetColumnMenu
              ? activeRegionViewStates[spreadsheetColumnMenu.region.id]
              : undefined
          }
          onClose={() => setSpreadsheetColumnMenu(null)}
          onFocusReturn={() => gridRef.current?.focus()}
          onChange={(state) => {
            if (spreadsheetColumnMenu) {
              changeActiveRegionView(spreadsheetColumnMenu.region.id, state);
            }
          }}
        />
      </Stack>
    </Tabs>
  );
});
